import { Localized } from "@fluent/react"
import { LoadingButton } from "@mui/lab"
import { ButtonGroup, CircularProgress, debounce, IconButton, Menu, MenuItem, Dialog, DialogContent, DialogActions, Popper, Box, ClickAwayListener, Button, Alert, TextField, DialogTitle, DialogContentText, Stack, Chip, Typography, Paper, Divider, Tooltip, ToggleButtonGroup, ToggleButton, Theme, SxProps, ButtonBase, Collapse } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { unwrapResult } from "@reduxjs/toolkit"
import clsx from "clsx"
import { createContext, forwardRef, memo, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { APIError, AppId } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { createConsequence, createDefect, createFunction, createReason, deleteConsequence, deleteDefect, deleteFunction, deleteReason, genConsequenceAI, genDefectAI, genReasonAI, generateFunctionsAI, loadRiskAnalysisContent, loadRiskAnalysisDrawing, loadRiskAnalysisRpnLimits, RiskAnalysisConsequence, RiskAnalysisContent, RiskAnalysisDefect, RiskAnalysisFunction, RiskAnalysisHeader, RiskAnalysisReason, RiskAnalysisRpnLimits, updateConsequence, updateDefect, updateFunction, updateReason, upsertRiskAnalysisDrawing, reorderRiskAnalysisFunctions } from "../documents/riskAnalysis/riskAnalysisApi"
import { showError, showSuccess } from "../notifications/notificationsSlice"
import './simpleRiskAnalysis.css'
import { isEditable } from "./utils"
import { JustText, useJustText } from "../../app/RichTextEditor"
import { Editor } from "@tiptap/react"
import { CommentsSection } from "./CommentsSection"
import { DocComment } from "../documents/commentsAPI"
import { RaCommentIndexContext, RaCommentScopeContext, RaCommentsCell } from "./RaCommentsCell"
import { anchorOf, buildRaCommentIndex, hasAnyRaComment, RaAnchor, rowAnchors } from "./raComments"
import { anchorKeyOf, createRaComposeStore, createRaSelectionStore, RaAnchoredCell, RaCanCommentContext, RaComposeContext, RaSelectionContext } from "./RaSelection"
import { isTestsEnabled } from "../documents/documentsApi"
import { ValidationsBrowser } from "../validations/ValidationsBrowser"
import { CreateValidationForm } from "../validations/CreateValidation"
import { EditValidationForm } from "../validations/EditValidation"
import { ServerErrorMsg } from "../../app/ServerErrorMsg"
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { selectContextStructure } from "../structure/contextStructureSlice"
import { delConsequence, delDefect, delReason, docActionsSelectors, docFnSelectors, docTestsSelectors, pushConsequence, pushDefect, pushReason, replaceConsequences, replaceReasons, selectDocument, setConsequence, setDefect, setFunctions, setReason } from "../documents/documentSlice"
import { impossible } from "../../app/Utils"
import { useLocalStorage } from "../../useLocalStorage"
import type { AppDispatch } from "../../app/store"
import { EditDocumentTestDialog } from "../documents/documentTests/dialogs/EditDocumentTestDialog"
import { DocumentTest, linkTestRelatedObject, unlinkTestRelatedObject } from "../documents/documentTests/documentTestsApi"
import LaunchIcon from '@mui/icons-material/Launch'
import LinkOffIcon from '@mui/icons-material/LinkOff'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import TableRowsIcon from '@mui/icons-material/TableRows'
import DeviceHubIcon from '@mui/icons-material/DeviceHub'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AddIcon from '@mui/icons-material/Add'
import HighlightIcon from '@mui/icons-material/Highlight'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { generateIshikawaSVG, Ishikawa } from "./Ishikawa"
import SyncIcon from '@mui/icons-material/Sync'
import { BackgroundProcessLauncher } from "../backgroundProcessing/BackgroundProcessLauncher"
import { Active, CollisionDetection, DndContext, DragEndEvent, DragStartEvent, DragOverlay, PointerSensor, closestCenter, pointerWithin, useDraggable, useDroppable, useSensor, useSensors } from "@dnd-kit/core"
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'

const itemWrapperClass = "ra-edit-box my-2 border-solid rounded-3xl border-2 border-gray-500 p-4"

const FnContext = createContext<number>(0)


const DocIdContext = createContext<AppId>('')

interface Limit {
    good: number
    warning: number
}
const LimitContext = createContext<Limit>({ good: 0, warning: 0 })

type CommentsSettings
    = { tag: 'no' }
    | { tag: 'yes', canAdd: boolean, canResolve: boolean }

const CommentsContext = createContext<CommentsSettings>({ tag: 'no' })

/**
 * Whether the FMEA table shows its comments column.
 *
 * Wider than `tag === 'yes'` on purpose, mirroring `showDocComments`: a reviewer
 * who may no longer comment must still be able to read the threads that are
 * there. The column then renders without a composer.
 */
const useShowRaComments = (): boolean => {
    const setting = useContext(CommentsContext)
    const index = useContext(RaCommentIndexContext)
    return setting.tag === 'yes' || hasAnyRaComment(index)
}

/** What the current user may do in a comments cell. */
const useRaCommentPerms = (): { canAdd: boolean, canResolve: boolean } => {
    const setting = useContext(CommentsContext)
    return setting.tag === 'yes'
        ? { canAdd: setting.canAdd, canResolve: setting.canResolve }
        : { canAdd: false, canResolve: false }
}

// The TESTS_ENABLED document setting. It gates the function-level test column in
// both FMEA views and the reason-level DQ/IQ/OQ/PQ chips, which are only
// meaningful when the qualification tests they refer to exist. Defaults to true so
// a component rendered outside the provider keeps today's behaviour.
const TestsEnabledContext = createContext<boolean>(true)

interface WithComments {
    comments: DocComment[]
}

interface Editable {
    editable: boolean
}

// The wada-skutek-przyczyna diagram omits the FMEA scoring; the from-URS
// FMEA-by-group view shows it. Threaded down Function -> Defect -> Consequence
// -> Reason so one set of components serves both, and the 'simple' default keeps
// the existing view byte-identical.
type LeafVariant = 'simple' | 'fmea'
interface WithVariant {
    variant?: LeafVariant
}

// Groups are managed from the document structure tree, not from this view, so
// nothing here creates, edits or deletes them.
type Entity
    = { type: 'function', data: RiskAnalysisFunction }

type DialogState
    = { tag: 'none' }
    | { tag: 'assoc-test', fn: RiskAnalysisFunction }
    // fn is optional: the tests view creates a test up front and leaves linking to
    // the drag, so the dialog opens with no related object preset.
    | { tag: 'create-test', fn?: RiskAnalysisFunction }
    // fn is optional: the tests view opens a test that may be linked to no function
    // at all, and the dialog's relatedObj is optional too.
    | { tag: 'edit-test', test: DocumentTest, fn?: RiskAnalysisFunction }
    | { tag: 'delete', item: Entity, parentGroupId?: AppId }

const DialogContext = createContext<(st: DialogState) => void>(() => { })

type View = 'diagram' | 'table' | 'ishikawa' | 'fmea-diagram' | 'tests'

const allViews: View[] = ['diagram', 'table', 'ishikawa', 'fmea-diagram', 'tests']

// One preference for every RA document rather than one per document: the point is
// that leaving a document and coming back keeps the view the user picked.
const viewStorageKey = 'RiskAnalysis_View'

/**
 * What is in storage cannot be trusted to be a view this document can show. It may
 * name a view an older build wrote, or 'ishikawa' for a document whose method is not
 * ISHIKAWA — and that toggle isn't rendered there, so honouring it would leave an
 * empty page with nothing selected in the switcher. A rejected value is left in
 * storage untouched, so the preference survives for documents that can honour it.
 *
 * 'tests' needs the same treatment for the same reason, and unlike 'table' and
 * 'fmea-diagram' — which merely lose their test column when tests are off, and are
 * deliberately left alone — that view has nothing at all to show without them.
 */
const resolveView = (stored: unknown, isIshikawa: boolean, testsEnabled: boolean): View => {
    const fallback: View = isIshikawa ? 'ishikawa' : 'diagram'
    if (!allViews.includes(stored as View)) {
        return fallback
    }
    if (stored === 'ishikawa' && !isIshikawa) {
        return fallback
    }
    return stored === 'tests' && !testsEnabled ? fallback : stored as View
}

const empty = new Set([])

const hasMissingRpnLimits = (limits: RiskAnalysisRpnLimits | null) => {
    if (!limits) {
        return true
    }

    const lowMax = limits.low?.maxValue
    const mediumMax = limits.medium?.maxValue

    if (lowMax == null || mediumMax == null) {
        return true
    }

    return lowMax === 0 && mediumMax === 0
}

interface RiskAnalysisContentProps {
    doc: RiskAnalysisContent
    sectionId: string
}
export const SimpleRiskAnalysisForm = (props: RiskAnalysisContentProps & WithComments) => {
    const { doc, comments, sectionId } = props
    const dispatch = useAppDispatch()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const documentContentActions = useAppSelector(state => docActionsSelectors.selectAll(state))
    const { id } = header

    // The document slice stores the generic DocumentHeader; for RA documents the
    // payload also carries the analysis method, which decides whether the
    // Ishikawa diagram is offered at all.
    const isIshikawa = (header as RiskAnalysisHeader).riskAnalysisMethod === 'ISHIKAWA'
    const editable = isEditable(documentContentActions)
    const testsEnabled = useMemo(() => isTestsEnabled(header.settings), [header.settings])

    const [dialog, setDialog] = useState<DialogState>({ tag: 'none' })
    const [running, setRunning] = useState(false)
    const [testId, setTestId] = useState<AppId | undefined>(undefined)
    const [storedView, setStoredView] = useLocalStorage<View>(viewStorageKey, isIshikawa ? 'ishikawa' : 'diagram')
    const view = resolveView(storedView, isIshikawa, testsEnabled)
    const [rpnLimits, setRpnLimits] = useState<RiskAnalysisRpnLimits | null>(null)
    const [rpnLimitsLoaded, setRpnLimitsLoaded] = useState(false)
    const [showMissingLimitsWarning, setShowMissingLimitsWarning] = useState(false)

    const reorder = useFunctionReorder(id)
    const testDnd = useTestLinkDnd(id, testsEnabled && editable)

    const selection: Set<AppId> = useMemo(() => testId ? new Set([testId]) : new Set(), [testId])

    useEffect(() => {
        const load = async () => {
            try {
                const result = await dispatch(loadRiskAnalysisRpnLimits({ docId: Number(id) })).unwrap()
                setRpnLimits(result)
                setShowMissingLimitsWarning(hasMissingRpnLimits(result))
            } catch (error) {
                const apiError = error as APIError

                if (apiError.kind === "http" && apiError.status === 404) {
                    setRpnLimits(null)
                    setShowMissingLimitsWarning(true)
                    return
                }

                dispatch(showError('error'))
                setShowMissingLimitsWarning(false)
            } finally {
                setRpnLimitsLoaded(true)
            }
        }

        load()
    }, [dispatch, id])

    const goodThreshold = rpnLimits?.low?.maxValue ?? 0

    const warningThreshold = rpnLimits?.medium?.maxValue ?? 0

    const limit = useMemo(() => {
        return { good: goodThreshold, warning: warningThreshold }
    }, [goodThreshold, warningThreshold])

    const commentsCfg = useMemo(() => {
        if (documentContentActions.findIndex(act => act === 'Comment') >= 0) {
            return {
                tag: 'yes',
                canAdd: true,
                canResolve: documentContentActions.findIndex(act => act === 'ResolveComment') >= 0,
            } as CommentsSettings
        } else {
            return {
                tag: 'no'
            } as CommentsSettings
        }
    }, [documentContentActions])

    // Grouped once here rather than filtered per cell: an inline filter returns a
    // fresh array on every call and defeats every memo below it, which is exactly
    // what made TestSection re-render all the rows on every store action.
    const commentIndex = useMemo(() => buildRaCommentIndex(comments), [comments])

    // The document plus the RA section guid — what a comment's sectionGuid should
    // hold. `sectionId` is a prop here, so unlike DocIdContext this form can both
    // provide it and have its children read it.
    const commentScope = useMemo(() => ({ documentId: id, sectionGuid: sectionId }), [id, sectionId])

    // Which comment is open, and so which fragment is highlighted. A store, not
    // state: see RaSelection.tsx for why a context value would be the wrong
    // shape here.
    const commentSelection = useMemo(createRaSelectionStore, [])
    // Which tile a new comment is being written about; same arrangement.
    const commentCompose = useMemo(createRaComposeStore, [])
    // Whether tiles carry the add-comment button — the rule useRaCommentPerms
    // applies, read here because RaSelection.tsx cannot import it.
    const canComment = commentsCfg.tag === 'yes' && commentsCfg.canAdd

    const openDialog = useCallback((st: DialogState) => {
        setDialog(st)
    }, [])
    const closeDialog = useCallback(() => {
        setDialog({
            tag: 'none'
        })
    }, [])

    const handleAssocTestClick = async () => {
        if (testId && dialog.tag === 'assoc-test') {
            setRunning(true)
            const fn = dialog.fn
            const { defects, ...data } = fn
            try {
                await dispatch(updateFunction({
                    ...data,
                    docId: id,
                    testDefinitionId: testId,
                    isTestable: true,
                })).unwrap()
            } catch (error) {

            } finally {
                setRunning(false)
                setDialog({ tag: 'none' })
                setTestId(undefined)
            }
        }
    }

    const handleCloseDialogClick = () => {
        setDialog({ tag: 'none' })
        setTestId(undefined)
    }

    useEffect(() => {
        console.log('Doc changed: ', doc)
    }, [doc])

    const handleViewChange = (_: React.MouseEvent<HTMLElement>, newView: View | null,) => {
        if (newView) {
            setStoredView(newView)
        }
    }

    const title: string =
        view === 'diagram' ? 'Diagram wada-skutek-przyczyna' :
            view === 'table' ? 'Analiza FMEA' :
                view === 'fmea-diagram' ? 'Diagram FMEA' :
                    view === 'tests' ? 'Testy i funkcje' :
                        'Diagram Ishikawy'


    return <DocIdContext.Provider value={id}>
        <LimitContext.Provider value={limit}>
            <RaCommentScopeContext.Provider value={commentScope}>
            <RaCommentIndexContext.Provider value={commentIndex}>
            <RaSelectionContext.Provider value={commentSelection}>
            <RaComposeContext.Provider value={commentCompose}>
            <RaCanCommentContext.Provider value={canComment}>
            <CommentsContext.Provider value={commentsCfg}>
                <TestsEnabledContext.Provider value={testsEnabled}>
                <DialogContext.Provider value={openDialog}>
                    {/* <div className="flex flex-row gap-x-4"> */}
                    {/* <div className="basis-full"> */}
                    <Box>
                        {rpnLimitsLoaded && showMissingLimitsWarning && <Alert sx={{ mb: 2 }} severity="warning">
                            <Localized id="risk-analysis-missing-rpn-limits">
                                <span>_Brak ustawionych limitow RPN dla poziomow low i medium.</span>
                            </Localized>
                        </Alert>}
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            py: .5,
                            px: 1,
                            bgcolor: 'var(--slate-50)',
                            border: '1px solid var(--gray-200)',
                            borderRadius: '.5rem',
                            alignItems: 'center',
                            mb: 1,
                        }}>
                            <span><b>{`${title}`}</b></span>
                            {/* Grouped so the switcher stays right-aligned whether or
                                not the generate button is showing. */}
                            <Box sx={{
                                ml: 'auto',
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                            }}>
                                {view !== 'ishikawa' && <GenerateFunctionsButton editable={editable} />}
                                <ToggleButtonGroup
                                    size='small'
                                    value={view}
                                    onChange={handleViewChange}
                                    exclusive>
                                    {isIshikawa && <Tooltip title='Diagram Ishikawy'>
                                        <ToggleButton size='small' value='ishikawa'>
                                            <DeviceHubIcon />
                                        </ToggleButton>
                                    </Tooltip>}
                                    <Tooltip title='Diagram wada-skutek-przyczyna'>
                                        <ToggleButton size="small" value='diagram'>
                                            <AccountTreeIcon />
                                        </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title='Tabela FMEA'>
                                        <ToggleButton size='small' value='table'>
                                            <TableRowsIcon />
                                        </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title='Diagram FMEA'>
                                        <ToggleButton size='small' value='fmea-diagram'>
                                            <AccountTreeIcon sx={{ transform: 'rotate(90deg)' }} />
                                        </ToggleButton>
                                    </Tooltip>
                                    {testsEnabled && <Tooltip title='Testy i funkcje'>
                                        <ToggleButton size='small' value='tests'>
                                            <FactCheckIcon />
                                        </ToggleButton>
                                    </Tooltip>}
                                </ToggleButtonGroup>
                            </Box>
                        </Box>
                        {(view === 'diagram' || view === 'table') &&
                            // The table's test cells sit inside this grid, and dnd-kit binds a
                            // draggable to its closest provider — so test linking here has to
                            // share the reorder context rather than nest its own. The two are
                            // kept apart by discriminating on the drag's type, never by
                            // touching each other's state. The plain diagram has no test
                            // column, so it only ever sees reorder drags.
                            <TestDndContextValue.Provider value={{ enabled: testDnd.enabled && view === 'table' }}>
                                <DndContext
                                    sensors={reorder.sensors}
                                    collisionDetection={raCollisionDetection}
                                    onDragStart={e => isTestDrag(e.active) ? testDnd.onDragStart(e) : reorder.onDragStart(e)}
                                    onDragEnd={e => isTestDrag(e.active) ? testDnd.onDragEnd(e) : reorder.onDragEnd(e)}
                                    // Must branch too: reorder's cancel restores a snapshot taken
                                    // in its own onDragStart, which a test drag never called — so
                                    // routing a cancelled test drag there would roll the function
                                    // list back to a stale order.
                                    onDragCancel={e => isTestDrag(e.active) ? testDnd.onDragCancel() : reorder.onDragCancel()}
                                >
                                    {reorder.isDirty && <ReorderBar saving={reorder.saving} onSave={reorder.onSave} onCancel={reorder.onCancel} />}
                                    {view === 'diagram' && <Content data={doc} comments={comments} sectionId={sectionId} reorder={reorder} />}
                                    {view === 'table' && <TableContent data={doc} comments={comments} sectionId={sectionId} reorder={reorder} />}
                                    <DragOverlay style={{ pointerEvents: 'none' }}>
                                        {testDnd.activeTest
                                            ? <TestDragPreview test={testDnd.activeTest} />
                                            : reorder.activeFn ? <FunctionDragPreview fn={reorder.activeFn} /> : null}
                                    </DragOverlay>
                                </DndContext>
                            </TestDndContextValue.Provider>}
                        {isIshikawa && view === 'ishikawa' && <IshikawaContent data={doc} comments={comments} sectionId={sectionId} />}
                        {view === 'fmea-diagram' && <FmeaDiagramContent comments={comments} sectionId={sectionId} />}
                        {view === 'tests' && <TestsViewContent />}
                    </Box>
                    {/* </div> */}
                    {/* </div> */}
                    {dialog.tag === 'create-test' && <EditDocumentTestDialog
                        documentId={Number.parseInt(id)}
                        sectionId={sectionId}
                        mode='create'
                        onCancel={closeDialog}
                        onSave={closeDialog}
                        relatedObjType={dialog.fn ? 'FUNCTION' : undefined}
                        relatedObjId={dialog.fn?.id}
                        relatedObj={dialog.fn ? {
                            tag: 'RA',
                            data: dialog.fn,
                            docId: id,
                            docSymbol: header.no,
                            loaded: true,
                        } : undefined}
                    />}
                    {dialog.tag === 'edit-test' && <EditDocumentTestDialog
                        testId={dialog.test.id}
                        documentId={Number.parseInt(id)}
                        sectionId={sectionId}
                        mode='edit'
                        onCancel={closeDialog}
                        onSave={closeDialog}
                        relatedObj={dialog.fn ? {
                            tag: 'RA',
                            data: dialog.fn,
                            docId: id,
                            docSymbol: header.no,
                            loaded: true,
                        } : undefined}
                    />}
                    {dialog.tag === 'delete' && <DeleteItem item={dialog.item} parentGroupId={dialog.parentGroupId} />}
                    <Dialog fullWidth maxWidth='lg' open={dialog.tag === 'assoc-test'}>
                        <DialogContent>
                            <ValidationsBrowser excluded={empty} selection={selection} itemChecked={id => setTestId(id)} itemUnchecked={id => setTestId(undefined)}></ValidationsBrowser>
                        </DialogContent>
                        <DialogActions>
                            <LoadingButton loading={running} onClick={handleAssocTestClick}>
                                <Localized id="ok"><span>OK</span></Localized>
                            </LoadingButton>
                            <LoadingButton loading={running} onClick={handleCloseDialogClick}>
                                <Localized id="cancel"><span>Anuluj</span></Localized>
                            </LoadingButton>
                        </DialogActions>
                    </Dialog>
                </DialogContext.Provider>
                </TestsEnabledContext.Provider>
            </CommentsContext.Provider>
            </RaCanCommentContext.Provider>
            </RaComposeContext.Provider>
            </RaSelectionContext.Provider>
            </RaCommentIndexContext.Provider>
            </RaCommentScopeContext.Provider>
        </LimitContext.Provider>
    </DocIdContext.Provider>
}

const FunctionInfo = (props: { fn: RiskAnalysisFunction }) => {
    const { fn } = props

    return <Paper sx={{
        p: 1,
        bgcolor: 'var(--gray-50)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
    }}>
        <small>Funkcja</small>
        <Box sx={{
            display: 'flex',
            gap: 2,
        }}>
            <Chip label={fn.numeration} />
            <Typography sx={{
                [`& p`]: {
                    mt: 0,
                },
            }} component='div' variant="body1" dangerouslySetInnerHTML={{ __html: fn.name }} />
        </Box>
    </Paper>
}

interface ContentProps {
    data: RiskAnalysisContent
    comments: DocComment[]
    sectionId: string
}

export interface FunctionReorderHandle {
    localFunctions: RiskAnalysisFunction[]
    isDirty: boolean
    saving: boolean
    activeFn: RiskAnalysisFunction | null
    sensors: ReturnType<typeof useSensors>
    onDragStart: (e: DragStartEvent) => void
    onDragEnd: (e: DragEndEvent) => void
    onDragCancel: () => void
    onSave: () => Promise<void>
    onCancel: () => void
}

// Shared drag-and-drop reorder logic for the function list. Both the diagram
// (Content) and FMEA table (TableContent) views consume the same handle so
// that local order and dirty state are preserved when switching views.
function useFunctionReorder(docId: AppId): FunctionReorderHandle {
    const dispatch = useAppDispatch()
    const serverFunctions = useAppSelector(state => docFnSelectors.selectAll(state))
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
    const [activeFn, setActiveFn] = useState<RiskAnalysisFunction | null>(null)
    const [localFunctions, setLocalFunctions] = useState<RiskAnalysisFunction[]>(serverFunctions)
    const [isDirty, setIsDirty] = useState(false)
    const [saving, setSaving] = useState(false)
    const snapshotRef = useRef<RiskAnalysisFunction[] | null>(null)

    // Mirror the latest local list in a ref so the drag handlers always read the
    // current order regardless of closure staleness.
    const localFunctionsRef = useRef(localFunctions)
    localFunctionsRef.current = localFunctions

    // Keep local list in sync with the server whenever there are no pending changes
    // (e.g. after a background AI generation refreshes the list).
    useEffect(() => {
        if (!isDirty) setLocalFunctions(serverFunctions)
    }, [serverFunctions, isDirty])

    const onDragStart = (e: DragStartEvent) => {
        snapshotRef.current = localFunctionsRef.current
        setActiveFn(localFunctionsRef.current.find(f => f.id === e.active.id) ?? null)
    }

    const onDragEnd = (e: DragEndEvent) => {
        setActiveFn(null)
        const { active, over } = e
        if (!over || active.id === over.id) return

        // Indices are resolved against the full local list so functions hidden
        // by a view's filter keep their relative position.
        const current = localFunctionsRef.current
        const from = current.findIndex(f => f.id === active.id)
        const to   = current.findIndex(f => f.id === over.id)
        if (from < 0 || to < 0 || from === to) return
        setLocalFunctions(arrayMove(current, from, to))
        setIsDirty(true)
    }

    const onDragCancel = () => {
        setActiveFn(null)
        if (snapshotRef.current) setLocalFunctions(snapshotRef.current)
        snapshotRef.current = null
    }

    // ReorderFunctionsAsync assigns Order = index + 1 over the ids it receives and
    // touches nothing else, so every function has to be in the payload — an omitted
    // one would keep a stale Order and collide with a rewritten one. The list is
    // flat, so the local order is the payload.
    const orderedForSave = (): { id: number }[] =>
        localFunctions.map(fn => ({ id: fn.id }))

    const onSave = async () => {
        setSaving(true)
        try {
            await dispatch(reorderRiskAnalysisFunctions({
                documentId: docId,
                functions: orderedForSave(),
            })).unwrap()
            dispatch(showSuccess('saved'))
            setIsDirty(false)
        } catch {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    const onCancel = () => {
        setLocalFunctions(serverFunctions)
        setIsDirty(false)
    }

    return { sensors, activeFn, onDragStart, onDragEnd, onDragCancel,
             localFunctions, isDirty, saving, onSave, onCancel }
}

// Strip shown below the view switcher when there are pending reorder changes.
const ReorderBar = ({ saving, onSave, onCancel }: { saving: boolean, onSave: () => void, onCancel: () => void }) =>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip
            color="warning"
            size="small"
            label={<Localized id="urs-reorder-mode-hint">Reordering mode — unsaved changes</Localized>}
        />
        <LoadingButton loading={saving} size="small" onClick={onCancel}>
            <Localized id="urs-reorder-cancel">Cancel</Localized>
        </LoadingButton>
        <LoadingButton loading={saving} size="small" variant="contained" onClick={onSave}>
            <Localized id="urs-reorder-apply">Apply changes</Localized>
        </LoadingButton>
    </Box>

// Compact preview shown in the DragOverlay while a function is being dragged.
const FunctionDragPreview = ({ fn }: { fn: RiskAnalysisFunction }) =>
    <Paper elevation={6} sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        opacity: 0.95,
        maxWidth: 600,
        fontSize: '0.75rem',
    }}>
        <DragIndicatorIcon fontSize='small' sx={{ color: 'action.disabled' }} />
        <Chip size='small' label={fn.numeration} />
        <Box sx={{ flex: 1, overflow: 'hidden' }} dangerouslySetInnerHTML={{ __html: fn.name }} />
    </Paper>

/**
 * Generating functions acts on the document, not on the view looking at it, so this
 * lives beside the view switcher and every mode reaches the same button. Ishikawa is
 * the exception the caller makes: it draws a cause-effect diagram rather than a
 * function list, so there is nothing there to generate into.
 */
const GenerateFunctionsButton = ({ editable }: Editable) => {
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const description = useAppSelector(state => state.document.doc?.header?.description ?? '')
    const hasDescription = stripHtml(description).trim().length > 0

    const [saving, setSaving] = useState(false)
    const [serverError, setServerError] = useState<APIError | null>(null)

    const handleBackgroundProcessCompleted = useCallback(async () => {
        try {
            await dispatch(loadRiskAnalysisContent(docId)).unwrap()
            dispatch(showSuccess('refreshed'))
        } catch {
            dispatch(showError('error'))
        }
    }, [dispatch, docId])

    const handleConfirmGenerateFunctionAIClick = async () => {
        if (!hasDescription) {
            return false
        }

        try {
            setSaving(true)

            const result = await dispatch(generateFunctionsAI(docId))

            if (generateFunctionsAI.fulfilled.match(result)) {
                setServerError(null)
                return true
            } else {
                if (result.payload && typeof result.payload === 'object' && 'kind' in result.payload) {
                    setServerError(result.payload as APIError)
                } else {
                    dispatch(showError('error'))
                }
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
        return false
    }

    if (!editable) {
        return null
    }

    return <Tooltip title='Generate functions by AI'>
        <span>
            <BackgroundProcessLauncher
                triggerLabel='Generate functions by AI'
                triggerButtonProps={{
                    variant: 'outlined',
                    startIcon: <AutoAwesomeIcon fontSize='small' />,
                    sx: { px: 1.5 },
                }}
                questionTitle={
                    <Localized id='generate-functions-by-ai'>
                        <span>Generate functions by AI</span>
                    </Localized>
                }
                questionDescription={
                    <Localized id='generate-functions-confirm'>
                        <span>Generate functions based on document description?</span>
                    </Localized>
                }
                trackingLabel='Generate functions by AI'
                questionChildren={<>
                    {!hasDescription && <Localized id='no-description-warning' elems={{}}>
                        <Alert severity='warning' sx={{ mt: 2 }}>
                            No document description. Fill in the description to generate functions.
                        </Alert>
                    </Localized>}
                    <Paper sx={{ mt: 2, p: 2, bgcolor: 'var(--gray-50)' }} variant='outlined'>
                        {hasDescription
                            ? <div className='rich' dangerouslySetInnerHTML={{ __html: description }} />
                            : <Localized id='no-description'>
                                <Typography color='text.secondary'>
                                    No document description.
                                </Typography>
                            </Localized>}
                    </Paper>
                    <ServerErrorMsg err={serverError} />
                </>}
                questionMaxWidth='md'
                loading={saving}
                confirmLabel='_generuj'
                confirmLabelId='generate'
                confirmColor='info'
                confirmStartIcon={<SyncIcon />}
                confirmDisabled={!hasDescription}
                objectType='DOCUMENT'
                objectId={docId}
                onStart={handleConfirmGenerateFunctionAIClick}
                onCompleted={handleBackgroundProcessCompleted}
                onOpenQuestion={() => setServerError(null)}
                onCloseQuestion={() => setServerError(null)}
            />
        </span>
    </Tooltip>
}

function Content(props: ContentProps & { reorder: FunctionReorderHandle }) {
    const { comments, sectionId, reorder } = props
    const docContentActions = useAppSelector(docActionsSelectors.selectAll)

    const { localFunctions } = reorder

    // Drawn from the pending local order so a drag shows its result immediately.
    const editable = isEditable(docContentActions)
    const rows = useFunctionRows(localFunctions, showUncriticalOrCritical, editable)
    const functions = useMemo(() => rowFunctions(rows), [rows])

    const gridRef = useRef<HTMLDivElement | null>(null)

    // Guarded on rows rather than on functions: a document with no functions still
    // produces the add row, and bailing out on that would drop the only in-place
    // way to create the first function.
    if (rows.length === 0) {
        return <Alert sx={{ minWidth: '50%' }} variant="outlined" color="warning">Dokument jest pusty.</Alert>
    } else {
        return <>
            {functions.length === 0 &&
                <Alert sx={{ mb: 2, minWidth: '50%' }} variant="outlined" color="warning">Dokument jest pusty.</Alert>}
            <div ref={gridRef} id='simple-risk-analysis-grid' className="border-solid border-gray-300 border">
                <SortableContext items={functions.map(f => f.id)} strategy={verticalListSortingStrategy}>
                    {rows.map(row => {
                        switch (row.kind) {
                            case 'add':
                                return <AddFunctionRow key={row.id} parentSectionGuid={sectionId} />
                            default:
                                return <Function key={row.fn.id} editable={editable} fn={row.fn} comments={comments} />
                        }
                    })}
                </SortableContext>
            </div>
        </>
    }
}

// `minmax(0, Nfr)` rather than a bare `Nfr`: `Nfr` means `minmax(auto, Nfr)`, so a
// track silently floors at its items' min-content width. Every grid that resolves
// this template holds different text and so floors differently, which is what pulls
// the header out of line with the rows once zooming in eats the spare width.
// TableColumnsHeader and FunctionCell both resolve this template through
// `subgrid`. The test column used to be a fifth track here; it now lives inside
// the details track (see innerGridColTemplate), so the details track absorbs its
// old 1fr when tests are on. It widens again when the comments column is on, so
// the columns inside it keep roughly the width they had before.
const outerGridColTemplate = (testsEnabled: boolean, showComments: boolean) => {
    const details = (showComments ? 6.5 : 5) + (testsEnabled ? 1 : 0)
    return `24px 36px minmax(0, 1fr) minmax(0, ${details}fr)`
}

// Nine data tracks, then the function-level test column, then comments — each of
// the last two only when on. Comments is deliberately last: RaCommentsCell pins
// itself to the final track, so any column added here goes before it.
//
// Every reason row emits exactly nine cells — DefectCell 1 + ConsequenceCell 2 +
// ReasonCell 6 — plus a comments cell. The test cell is emitted once per
// function by FunctionCellBody and spans all of the function's rows
// (fnRowSpan). A column added here needs a matching cell and a heading in
// TableColumnsHeader; all of them resolve this same function, which is what
// keeps the header on the column edges the rows use.
const innerGridColTemplate = (testsEnabled: boolean, showComments: boolean) =>
    'minmax(0, 1fr) minmax(0, 1fr) 48px minmax(0, 1fr) 48px minmax(0, 1fr) 48px 48px minmax(0, 1fr)'
    + (testsEnabled ? ' minmax(0, 1.2fr)' : '')
    + (showComments ? ' minmax(0, 1.5fr)' : '')

/** Inner-grid track of the test column: right after the nine data tracks. */
const TESTS_TRACK = 10

// The diagram views' counterpart. Their data tracks are the ones
// simpleRiskAnalysis.css declares for `.ra-fn-details` / `.ra-fmea .ra-fn-details`
// — kept there because NewRisk.tsx still relies on that rule — and repeated here
// so the test and comments tracks can be appended. Same order as the table: data,
// then tests (FMEA diagram only; the plain diagram has no test column), then
// comments, which RaCommentsCell pins to the last track. Header and body each
// apply this inline, so the two can't drift apart.
const diagramDataTracks: Record<LeafVariant, number> = { simple: 8, fmea: 11 }
const diagramDataTemplate: Record<LeafVariant, string> = {
    simple: '36px 1fr 36px 1fr 36px 1fr 36px 1fr',
    fmea: '36px 1fr 36px 1fr 98px 1fr 66px 1fr 66px 66px 1.2fr',
}
const diagramGridColTemplate = (variant: LeafVariant, showTests: boolean, showComments: boolean) =>
    diagramDataTemplate[variant]
    + (showTests ? ' minmax(0, 1.4fr)' : '')
    + (showComments ? ' minmax(0, 1.5fr)' : '')

// Divides the comments column from the diagram without boxing it like a table:
// every diagram row, placeholders included, emits one of these, so the left
// edges join into a single rule.
const diagramCommentCellSx: SxProps<Theme> = {
    borderLeft: '1px solid var(--gray-300)',
    px: 1,
    py: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
}

const table_border = '1px solid var(--gray-400)'
const TableColumnsHeader = () => {
    const testsEnabled = useContext(TestsEnabledContext)
    const showComments = useShowRaComments()

    return <Box sx={{
        // Rendered inside the rows grid, so it has to span every track rather
        // than sit in the first one.
        gridColumn: '1 / -1',
        display: 'grid',
        // Inherits the container's resolved tracks instead of re-resolving the same
        // template in a grid of its own, so the headings sit on exactly the column
        // edges the rows use. FunctionCell does the same.
        gridTemplateColumns: 'subgrid',
        // Only the bottom edge: the container draws the table's left and top.
        borderBottom: table_border,
        [`& > div`]: {
            fontSize: '0.75rem',
            lineHeight: '1rem',
            fontWeight: 600,
            // p: .5,
            bgcolor: 'var(--gray-100)',
            // A grid item's own `min-width: auto` would overflow the track that
            // minmax(0, …) just allowed to shrink, so headings wrap instead.
            minWidth: 0,
            overflowWrap: 'anywhere',
        },
    }}>
        <Box sx={{
            borderRight: table_border,
            p: .5,
        }} />
        <Box sx={{
            borderRight: table_border,
            p: .5,
        }}>Lp.</Box>
        <Box sx={{
            borderRight: table_border,
            p: .5,
        }}>Funkcja</Box>
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: innerGridColTemplate(testsEnabled, showComments),
            [`& > div`]: {
                fontSize: '0.75rem',
                lineHeight: '1rem',
                fontWeight: 600,
                p: .5,
                bgcolor: 'var(--gray-100)',
                borderRight: table_border,
                // As on the outer grid; overflowWrap is inherited from the rule on
                // the outer header cells, but min-width has to be set per item.
                minWidth: 0,
            },
        }}>
            <Box>Potencjalna wada</Box>
            <Box>Skutek</Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                S
            </Box>
            <Box>Przyczyna</Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                W
            </Box>
            <Box>Obecne środki kontroli</Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                D
            </Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                P
            </Box>
            <Box>Zalecane działania</Box>
            {testsEnabled && <Box>Test(y)</Box>}
            {showComments && <Box><Localized id='ra-comments-column'>Komentarze</Localized></Box>}
        </Box>
    </Box>
}

function TableContent(props: ContentProps & { reorder: FunctionReorderHandle }) {
    const { comments, sectionId, reorder } = props
    const testsEnabled = useContext(TestsEnabledContext)
    const showComments = useShowRaComments()
    const docContentActions = useAppSelector(docActionsSelectors.selectAll)
    const editable = isEditable(docContentActions)

    const { localFunctions } = reorder

    // The diagram view's filter rather than the critical-only one this view used
    // before: a function created from the add row starts with no defects, and a
    // critical-only filter would swallow it the instant it was added.
    const rows = useFunctionRows(localFunctions, showUncriticalOrCritical, editable)
    const functions = useMemo(() => rowFunctions(rows), [rows])

    return <>
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: outerGridColTemplate(testsEnabled, showComments),
            // The table's outer left and top edges; every cell draws its own right
            // and bottom, so these are the only two nothing else accounts for.
            borderLeft: table_border,
            borderTop: table_border,
            fontSize: '0.75rem',
        }}>
            <SortableContext items={functions.map(f => f.id)} strategy={verticalListSortingStrategy}>
                <TableColumnsHeader />
                {rows.map(row => {
                    switch (row.kind) {
                        case 'add':
                            // The container is a real grid, so this has to span every
                            // track rather than land in the first one; the diagram views
                            // can drop AddFunctionRow straight in because their container
                            // is a plain block.
                            return <Box
                                key={row.id}
                                sx={{
                                    gridColumn: '1 / -1',
                                    borderRight: table_border,
                                    borderBottom: table_border,
                                }}
                            >
                                <AddFunctionRow parentSectionGuid={sectionId} />
                            </Box>
                        default:
                            return <FunctionCell key={row.fn.id} fn={row.fn} comments={comments} editable={editable} />
                    }
                })}
            </SortableContext>
        </Box>
    </>
}

function stripHtml(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return div.textContent ?? div.innerText ?? ''
}

function IshikawaContent(props: ContentProps) {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)

    const functions = useAppSelector(state => docFnSelectors.selectAll(state))
    const docTitle = useAppSelector(state => state.document.doc?.header?.title ?? '')

    const docTitleRef = useRef(docTitle)
    useEffect(() => { docTitleRef.current = docTitle }, [docTitle])

    const saveDrawing = async (currentFunctions: RiskAnalysisFunction[]) => {
        const svgData = generateIshikawaSVG(currentFunctions, docTitleRef.current)
        if (svgData) {
            await dispatch(upsertRiskAnalysisDrawing({
                docId: Number(docId),
                format: 'svg',
                drawingData: svgData,
            })).unwrap()
        }
    }

    const onAddFunction = async (name: string): Promise<void> => {
        const newFn = await dispatch(createFunction({
            documentId: docId,
            name,
            criticality: '',
            isTestable: false,
            currentControlMethod: '',
            testDefinitionId: null,
            parentSectionGuid: sectionId,
        })).unwrap()
        await saveDrawing([...functions, newFn])
    }

    const onUpdateFunction = async (id: number, name: string): Promise<void> => {
        const fn = functions.find(f => f.id === id)
        if (!fn) return
        const { defects, ...data } = fn
        await dispatch(updateFunction({ ...data, name, docId })).unwrap()
        await saveDrawing(functions.map(f => f.id === id ? { ...f, name } : f))
    }

    const onDeleteFunction = async (id: number): Promise<void> => {
        await dispatch(deleteFunction({ docId, functionId: id })).unwrap()
        await saveDrawing(functions.filter(f => f.id !== id))
    }

    const onAddDefect = async (functionId: number, name: string): Promise<void> => {
        const newDefect = await dispatch(createDefect({
            docId,
            name,
            functionId,
            value: 1,
        })).unwrap()
        const newConsequence = await dispatch(createConsequence({
            docId,
            name: '',
            defectId: newDefect.id,
            value: 0,
        })).unwrap()
        newDefect.consequences.push(newConsequence)
        const newReason = await dispatch(createReason({
            docId,
            name: '',
            consequenceId: newConsequence.id,
            value: 0,
            currentControlMethod: '',
        })).unwrap()
        newConsequence.reasons = [newReason]
        newDefect.functionId = functionId
        dispatch(pushDefect(newDefect))
        await saveDrawing(functions.map(f => f.id === functionId ? { ...f, defects: [...f.defects, newDefect] } : f))
    }

    const onGenAI = async (fn: RiskAnalysisFunction) => {
        try {
            const fnId = fn.id
            const defects = await dispatch(genDefectAI({ docId, fnId })).unwrap()
            for (let i = 0; i < defects.length; i++) {
                let defect = defects[i]
                defect.functionId = fnId
                dispatch(pushDefect(defect))
            }
            await saveDrawing(functions.map(f => f.id === fnId ? { ...f, defects: [...f.defects, ...defects] } : f))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const onUpdateDefect = async (id: number, name: string): Promise<void> => {
        const fn = functions.find(f => f.defects.some(d => d.id === id))
        if (!fn) return
        const defect = fn.defects.find(d => d.id === id)
        if (!defect) return
        await dispatch(updateDefect({ docId, id, functionId: fn.id, name, value: defect.value })).unwrap()
        dispatch(setDefect({ ...defect, name, functionId: fn.id }))
        await saveDrawing(functions.map(f => ({ ...f, defects: f.defects.map(d => d.id === id ? { ...d, name } : d) })))
    }

    const onDeleteDefect = async (id: number): Promise<void> => {
        const fn = functions.find(f => f.defects.some(d => d.id === id))
        if (!fn) return
        await dispatch(deleteDefect({ docId, defectId: id })).unwrap()
        dispatch(delDefect({ fnId: fn.id, defectId: id }))
        await saveDrawing(functions.map(f => ({ ...f, defects: f.defects.filter(d => d.id !== id) })))
    }

    const onToggleCritical = async (id: number, critical: boolean): Promise<void> => {
        const fn = functions.find(f => f.defects.some(d => d.id === id))
        if (!fn) return
        const defect = fn.defects.find(d => d.id === id)
        if (!defect) return
        const value = critical ? 1 : 0
        await dispatch(updateDefect({ docId, id, functionId: fn.id, name: defect.name, value })).unwrap()
        dispatch(setDefect({ ...defect, value, functionId: fn.id }))
        await saveDrawing(functions.map(f => ({ ...f, defects: f.defects.map(d => d.id === id ? { ...d, value } : d) })))
    }

    return <Ishikawa
        functions={functions}
        title={docTitle}
        onAddFunction={onAddFunction}
        onUpdateFunction={onUpdateFunction}
        onDeleteFunction={onDeleteFunction}
        onAddDefect={onAddDefect}
        onUpdateDefect={onUpdateDefect}
        onDeleteDefect={onDeleteDefect}
        onToggleCritical={onToggleCritical}
        onGenAI={onGenAI}
    />
}

// ---------------------------------------------------------------------------
// The from-URS FMEA view, sourced entirely from redux.
//
// Functions still carry a group in the database (RiskAnalysisFunctions.SectionGuid,
// set once at creation and never reassigned), but no view renders those groups any
// more: every list here is flat, ordered by the document-wide Order, and the report
// was flattened to match. Groups remain only as document sections in the structure
// tree.
// ---------------------------------------------------------------------------

// A render sequence for the function list. Still a row union rather than a plain
// array because two of the three views put these straight into a CSS grid, where
// the cells have to be direct children of the container — so the add button has
// to travel in the same sequence as the functions.
type RaRow
    = { kind: 'fn', fn: RiskAnalysisFunction }
    | { kind: 'add', id: string }

/**
 * The flat, filtered function list, drawn from `source` so a view can pass its
 * pending local reorder rather than the stored order.
 */
function useFunctionRows(
    source: RiskAnalysisFunction[],
    filter: (fn: RiskAnalysisFunction) => boolean,
    // Only an editable view offers the add-function button.
    withAddRow = false,
): RaRow[] {
    return useMemo(() => {
        const rows: RaRow[] = source.filter(filter).map(fn => ({ kind: 'fn', fn }))
        if (withAddRow) {
            rows.push({ kind: 'add', id: 'add-root' })
        }
        return rows
    }, [source, filter, withAddRow])
}

const rowFunctions = (rows: RaRow[]): RiskAnalysisFunction[] =>
    rows.flatMap(r => r.kind === 'fn' ? [r.fn] : [])

/**
 * The smallest tree that fills a whole FMEA row: one defect, one consequence under
 * it, one reason under that. Both the add-function row and the per-function "+"
 * build it, so the two entry points cannot drift apart.
 *
 * The returned defect already carries the nested consequence and reason and has its
 * functionId set, so it is ready to hand to pushDefect — left to the caller, which
 * knows when the row should appear.
 */
async function createEmptyDefectChain(
    dispatch: AppDispatch,
    docId: AppId,
    functionId: number,
): Promise<RiskAnalysisDefect> {
    const newDefect = await dispatch(createDefect({
        docId,
        name: '',
        functionId,
        // Critical, matching what the per-function "+" has always created: the
        // table view filters on criticality, so a 0 here would hide the new row.
        value: 1,
    })).unwrap()
    const newConsequence = await dispatch(createConsequence({
        docId,
        name: '',
        defectId: newDefect.id,
        value: 0,
    })).unwrap()
    newDefect.consequences.push(newConsequence)
    const newReason = await dispatch(createReason({
        docId,
        name: '',
        consequenceId: newConsequence.id,
        value: 0,
        currentControlMethod: '',
    })).unwrap()
    newConsequence.reasons = [newReason]
    newDefect.functionId = functionId
    return newDefect
}

/**
 * Adds a function to the RA section itself. Self-contained so every view can drop
 * it straight into its row loop. AddFunction still requires a parentSectionGuid
 * and rejects a null one, so the RA section id is what gets sent — new functions
 * are no longer placed in a group.
 */
const AddFunctionRow = ({ parentSectionGuid }: {
    parentSectionGuid: AppId
}) => {
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const [saving, setSaving] = useState(false)

    const handleClick = async () => {
        setSaving(true)
        try {
            const fn = await dispatch(createFunction({
                documentId: docId,
                name: '',
                criticality: '',
                isTestable: false,
                currentControlMethod: '',
                testDefinitionId: null,
                parentSectionGuid,
            })).unwrap()

            // A bare function has no FMEA row to render: the table view draws a line
            // of "empty" placeholders and offers no way to start one. Seeding the
            // smallest complete row gives every view something editable straight away.
            const defect = await createEmptyDefectChain(dispatch, docId, fn.id)
            dispatch(pushDefect(defect))
        } catch {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    return <Box sx={{ my: 1, ml: 1 }}>
        <LoadingButton loading={saving} onClick={handleClick} size='small' variant='outlined'>
            + Dodaj funkcję
        </LoadingButton>
    </Box>
}

// Module-level so its identity is stable across renders and useFunctionRows'
// memo actually holds. Every view filters the same way: a function with defects
// but none critical has been assessed and found harmless, so it leaves the FMEA.
const showUncriticalOrCritical = (fn: RiskAnalysisFunction) =>
    fn.defects.length === 0 || fn.defects.some(d => d.value === 1)

/**
 * Criticality is an Ishikawa-only concept: its diagram owns the checkbox that sets
 * it, and every other view both creates and shows critical defects only. So a
 * non-critical defect — unchecked in Ishikawa, or left behind by data that predates
 * this rule — is simply not rendered outside that view.
 */
const criticalDefects = (fn: RiskAnalysisFunction) => fn.defects.filter(d => d.value === 1)

// Dragging a test onto another function's test container links it there — the test
// is not moved, it ends up covering both functions. Deliberately a separate
// DndContext from useFunctionReorder: dnd-kit binds a draggable to its closest
// provider, so this can only stay independent in a view that has no reorder
// context of its own. That is true of the FMEA diagram and not of the FMEA table.
const TEST_DRAG_TYPE = 'ra-test'
const TEST_DROP_TYPE = 'ra-test-target'
const testDropId = (fnId: number) => `ra-test-target-${fnId}`

const isTestDrag = (active: Active | null) =>
    (active?.data.current as TestDragData | undefined)?.type === TEST_DRAG_TYPE

// One DndContext can only carry one collision strategy, but the two drags want
// different ones — and, more importantly, must not see each other's targets. A
// function drag that resolved to a test container would report an `over` the
// reorder handler can't find in its list and would silently do nothing; a test
// drag that resolved to a sortable row would have no fnId to link to.
const raCollisionDetection: CollisionDetection = args => {
    const testDrag = isTestDrag(args.active)
    const droppableContainers = args.droppableContainers.filter(
        c => (c.data.current?.type === TEST_DROP_TYPE) === testDrag)

    // pointerWithin for tests: the containers are tall, and closestCenter would
    // link to a neighbouring row whose centre happened to be nearer the cursor.
    return testDrag
        ? pointerWithin({ ...args, droppableContainers })
        : closestCenter({ ...args, droppableContainers })
}

const TestDragPreview = ({ test }: { test: DocumentTest }) =>
    <Paper elevation={3} sx={{ px: 1.5, py: .75, fontSize: '0.75rem', maxWidth: 260 }}>
        {test.testSpecification?.name ?? 'Test'}
    </Paper>

interface TestDragData {
    type: typeof TEST_DRAG_TYPE
    test: DocumentTest
    fromFnId: number
}

// docId is a parameter rather than a DocIdContext read — as in useFunctionReorder —
// because SimpleRiskAnalysisForm calls this hook while *providing* that context, and
// a component cannot consume a context it provides. Reading it there would silently
// yield the default '' and POST to document 0.
function useTestLinkDnd(docId: AppId, enabled: boolean) {
    const dispatch = useAppDispatch()
    // A larger threshold than the reorder sensor's 5px: the test container holds
    // buttons and links, and a stray pixel of movement while clicking one of them
    // should not start a drag.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
    const [activeTest, setActiveTest] = useState<DocumentTest | null>(null)

    const onDragStart = (e: DragStartEvent) => {
        const data = e.active.data.current as TestDragData | undefined
        setActiveTest(data?.type === TEST_DRAG_TYPE ? data.test : null)
    }

    const onDragCancel = () => setActiveTest(null)

    const onDragEnd = async (e: DragEndEvent) => {
        setActiveTest(null)
        const { active, over } = e
        const data = active.data.current as TestDragData | undefined
        if (!over || data?.type !== TEST_DRAG_TYPE) return

        const targetFnId = over.data.current?.fnId as number | undefined
        // Dropping a test back where it came from, or onto a function it already
        // covers, is a no-op rather than an error.
        if (targetFnId === undefined || targetFnId === data.fromFnId) return
        if (linkedFunctionIds(data.test).includes(targetFnId)) return

        try {
            await dispatch(linkTestRelatedObject({
                documentId: Number(docId),
                testId: data.test.id,
                relatedObjectType: 'FUNCTION',
                relatedObjectId: targetFnId,
            })).unwrap()
            dispatch(showSuccess('saved'))
        } catch {
            dispatch(showError('error'))
        }
    }

    return { enabled, sensors, activeTest, onDragStart, onDragEnd, onDragCancel }
}

// A test carries its links in relatedObjectIds; payloads from a backend without
// the link table still only have the single relatedObjectId. As in TestSection,
// an empty list is meaningful and must not fall through to the scalar.
const linkedFunctionIds = (test: DocumentTest): number[] =>
    test.relatedObjectIds !== undefined
        ? test.relatedObjectIds
        : (test.relatedObjectId !== null ? [test.relatedObjectId] : [])

function FmeaDiagramContent(props: WithComments & { sectionId: string }) {
    const { comments, sectionId } = props
    const allFunctions = useAppSelector(docFnSelectors.selectAll)
    const documentContentActions = useAppSelector(docActionsSelectors.selectAll)
    const editable = isEditable(documentContentActions)
    const testsEnabled = useContext(TestsEnabledContext)
    const docId = useContext(DocIdContext)
    const testDnd = useTestLinkDnd(docId, testsEnabled && editable)

    const rows = useFunctionRows(allFunctions, showUncriticalOrCritical, editable)

    if (rows.length === 0) {
        return <Alert severity='warning' variant='outlined'>Dokument jest pusty.</Alert>
    }

    return <>
        <TestDndContext value={testDnd}>
            <div className='ra-grid border-solid border-gray-300 border'>
                {rows.map(row => {
                    switch (row.kind) {
                        case 'add':
                            return <AddFunctionRow key={row.id} parentSectionGuid={sectionId} />
                        default:
                            return <Function key={row.fn.id} fn={row.fn} comments={comments} editable={editable} sortable={false} variant="fmea" />
                    }
                })}
            </div>
        </TestDndContext>
    </>
}

type TestDndHandle = ReturnType<typeof useTestLinkDnd>

// Off by default, so TestSection/TestItem stay inert in the views that do not
// mount the provider (the FMEA table, whose own reorder context would capture
// them, and the plain diagram, which has no test column at all).
const TestDndContextValue = createContext<{ enabled: boolean }>({ enabled: false })

// Mounts the DndContext only when linking is actually available, so no drag
// machinery is attached for read-only users or documents with tests turned off.
const TestDndContext = ({ value, children }: { value: TestDndHandle, children: ReactNode }) => {
    if (!value.enabled) {
        return <>{children}</>
    }

    return <TestDndContextValue.Provider value={{ enabled: true }}>
        <DndContext
            sensors={value.sensors}
            // pointerWithin, not the reorder view's closestCenter: the drop targets
            // are large containers, and closestCenter would happily link to a
            // neighbouring row whose centre happens to be nearer the cursor.
            collisionDetection={pointerWithin}
            onDragStart={value.onDragStart}
            onDragEnd={value.onDragEnd}
            onDragCancel={value.onDragCancel}
        >
            {children}
            <DragOverlay style={{ pointerEvents: 'none' }}>
                {value.activeTest ? <TestDragPreview test={value.activeTest} /> : null}
            </DragOverlay>
        </DndContext>
    </TestDndContextValue.Provider>
}

// ─── The tests view ──────────────────────────────────────────────────────────
//
// The same link, dragged the other way round: here a *function* is the draggable
// and a *test* the drop target. The FMEA views can only start from a function you
// can already see, which leaves a test linked to nothing invisible with no way
// back — this view lists every test in the document, so an orphan can be
// re-attached.
//
// Deliberately distinct drag/drop type tags from TEST_DRAG_TYPE/TEST_DROP_TYPE:
// the two directions never share a DndContext, but keeping the tags apart means a
// stray droppable could not resolve for the wrong drag even if they ever did.
const FN_LINK_DRAG_TYPE = 'ra-fn-link'
const FN_LINK_DROP_TYPE = 'ra-fn-link-target'
const fnLinkDropId = (testId: number) => `ra-fn-link-target-${testId}`

interface FnLinkDragData {
    type: typeof FN_LINK_DRAG_TYPE
    fn: RiskAnalysisFunction
}

interface FnLinkDropData {
    type: typeof FN_LINK_DROP_TYPE
    test: DocumentTest
}

// Mirrors useTestLinkDnd, and takes docId as a parameter for the same reason.
function useFunctionLinkDnd(docId: AppId, enabled: boolean) {
    const dispatch = useAppDispatch()
    // 8px like the test drag, not the reorder sensor's 5: both the function cards
    // and the test rows carry buttons, and a stray pixel while clicking one of
    // them should not start a drag.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))
    const [activeFn, setActiveFn] = useState<RiskAnalysisFunction | null>(null)

    const onDragStart = (e: DragStartEvent) => {
        const data = e.active.data.current as FnLinkDragData | undefined
        setActiveFn(data?.type === FN_LINK_DRAG_TYPE ? data.fn : null)
    }

    const onDragCancel = () => setActiveFn(null)

    const onDragEnd = async (e: DragEndEvent) => {
        setActiveFn(null)
        const { active, over } = e
        const data = active.data.current as FnLinkDragData | undefined
        if (!over || data?.type !== FN_LINK_DRAG_TYPE) {
            return
        }

        const drop = over.data.current as FnLinkDropData | undefined
        if (drop?.type !== FN_LINK_DROP_TYPE) {
            return
        }
        // Dropping a function the test already covers is a no-op rather than an
        // error — the link table's key would reject the duplicate anyway.
        if (linkedFunctionIds(drop.test).includes(data.fn.id)) {
            return
        }

        try {
            await dispatch(linkTestRelatedObject({
                documentId: Number(docId),
                testId: drop.test.id,
                relatedObjectType: 'FUNCTION',
                relatedObjectId: data.fn.id,
            })).unwrap()
            dispatch(showSuccess('saved'))
        } catch {
            dispatch(showError('error'))
        }
    }

    return { enabled, sensors, activeFn, onDragStart, onDragEnd, onDragCancel }
}

// fn.name is rich text from the editor, not a plain string — FunctionDragPreview
// and the FMEA views already render it as HTML. The paragraph reset has to be
// repeated here: simpleRiskAnalysis.css scopes its `p { margin: 0 }` to .ra-grid,
// and this view is not inside one.
const FunctionName = ({ fn }: { fn: RiskAnalysisFunction }) =>
    <Box
        sx={{
            minWidth: 0,
            overflowWrap: 'anywhere',
            '& p': { m: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: fn.name }} />

function TestsViewContent() {
    const docId = useContext(DocIdContext)
    const dialog = useContext(DialogContext)
    const testsEnabled = useContext(TestsEnabledContext)
    const documentContentActions = useAppSelector(docActionsSelectors.selectAll)
    const editable = isEditable(documentContentActions)
    const tests = useAppSelector(docTestsSelectors.selectAll)
    const functions = useAppSelector(docFnSelectors.selectAll)

    const dnd = useFunctionLinkDnd(docId, testsEnabled && editable)

    // One function at a time: the point is to answer "which tests cover this
    // function", and several highlights at once would put that back on the reader.
    // Read-only, so it stays available on a document nobody can edit.
    const [highlightedFnId, setHighlightedFnId] = useState<number | null>(null)
    const toggleHighlight = useCallback(
        (fnId: number) => setHighlightedFnId(current => current === fnId ? null : fnId),
        [])

    // Counted once over the tests rather than per card, which would be a scan of
    // every test for every function.
    const testCountByFn = useMemo(() => {
        const counts = new Map<number, number>()
        for (const t of tests) {
            for (const fnId of linkedFunctionIds(t)) {
                counts.set(fnId, (counts.get(fnId) ?? 0) + 1)
            }
        }
        return counts
    }, [tests])

    const body = <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ ...panelColumnSx, flex: '1.6 1 0' }}>
            <PanelHeading
                count={tests.length}
                action={editable && <Button
                    size='small'
                    variant='outlined'
                    startIcon={<AddIcon />}
                    onClick={() => dialog({ tag: 'create-test' })}
                >
                    Nowy test
                </Button>}
            >
                Testy
            </PanelHeading>
            <Box sx={panelListSx}>
                {tests.length === 0
                    ? <Alert severity='info' variant='outlined'>Dokument nie ma jeszcze testów.</Alert>
                    : tests.map(t => <TestLinkRow
                        key={t.id}
                        test={t}
                        functions={functions}
                        linkEnabled={dnd.enabled}
                        highlightedFnId={highlightedFnId} />)}
            </Box>
        </Box>
        <Box sx={{ ...panelColumnSx, flex: '1 1 0' }}>
            <PanelHeading count={functions.length}>Funkcje</PanelHeading>
            {dnd.enabled && <Box sx={{
                fontSize: '.75rem',
                color: 'text.secondary',
                display: 'flex',
                alignItems: 'center',
                gap: .5,
            }}>
                <DragIndicatorIcon sx={{ fontSize: '1rem' }} />
                Przeciągnij funkcję na test, aby ją z nim powiązać.
            </Box>}
            <Box sx={panelListSx}>
                {functions.length === 0
                    ? <Alert severity='warning' variant='outlined'>Dokument jest pusty.</Alert>
                    : functions.map(fn => <LinkableFunctionCard
                        key={fn.id}
                        fn={fn}
                        linkEnabled={dnd.enabled}
                        highlighted={highlightedFnId === fn.id}
                        testCount={testCountByFn.get(fn.id) ?? 0}
                        onToggleHighlight={toggleHighlight} />)}
            </Box>
        </Box>
    </Box>

    if (!dnd.enabled) {
        return body
    }

    return <DndContext
        sensors={dnd.sensors}
        // Only one kind of drag lives here, so no need for raCollisionDetection.
        // pointerWithin for the same reason as the test drag: the test rows are
        // tall, and closestCenter would link to a neighbour whose centre happens
        // to be nearer the cursor.
        collisionDetection={pointerWithin}
        onDragStart={dnd.onDragStart}
        onDragEnd={dnd.onDragEnd}
        onDragCancel={dnd.onDragCancel}
    >
        {body}
        <DragOverlay style={{ pointerEvents: 'none' }}>
            {dnd.activeFn ? <FunctionDragPreview fn={dnd.activeFn} /> : null}
        </DragOverlay>
    </DndContext>
}

// Both panels are their own scroll container, so a long test list and a long
// function list are navigable independently and neither drags the page with it.
// Sticky keeps them pinned while the surrounding document scrolls.
const panelColumnSx = {
    minWidth: 0,
    position: 'sticky',
    top: 8,
    maxHeight: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
} as const

// Only the list scrolls: the heading, its count and the Nowy test button stay put.
// minHeight 0 is what lets a flex child shrink below its content and scroll at all.
// dnd-kit auto-scrolls whichever of the two the pointer is over during a drag.
const panelListSx = {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    // Room for the cards' hover shadow, which a flush scroll container would clip
    // — and, because overflow-x resolves to auto here, turn into a stray
    // horizontal scrollbar.
    px: .25,
    // The cards are flex items, so they shrink to fit a capped column instead of
    // overflowing it — and a squashed list never scrolls. The test card hides it
    // worst: its own `overflow: hidden` (which rounds off the header band) drops
    // its automatic min-height from min-content to 0, so it collapses silently and
    // clips its functions rather than pushing the list past the bottom edge.
    '& > *': { flexShrink: 0 },
} as const

const PanelHeading = ({ count, action, children }: {
    count: number
    action?: ReactNode
    children: ReactNode
}) =>
    <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: .75,
        pb: .5,
        minHeight: 32,
        borderBottom: '1px solid',
        borderColor: 'divider',
    }}>
        <Box sx={{ fontWeight: 700, fontSize: '.9rem' }}>{children}</Box>
        <CountBadge value={count} />
        {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Box>

// A zero in filled primary reads as an alert, so nothing is the muted variant.
const CountBadge = ({ value }: { value: number }) =>
    <Box sx={{
        fontSize: '.65rem',
        fontWeight: 700,
        lineHeight: 1,
        px: .625,
        py: .375,
        borderRadius: 5,
        minWidth: 8,
        textAlign: 'center',
        bgcolor: value === 0 ? 'action.selected' : 'primary.main',
        color: value === 0 ? 'text.disabled' : 'primary.contrastText',
    }}>
        {value}
    </Box>

const FieldLabel = ({ children }: { children: ReactNode }) =>
    <Box sx={{
        fontSize: '.7rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.04em',
        color: 'text.secondary',
    }}>
        {children}
    </Box>

const Dash = () => <Box sx={{ fontSize: '.8rem', color: 'text.disabled' }}>—</Box>

// Amber, deliberately not the primary used by the drop states: a highlight and a
// pending link are different things and would otherwise be the same colour.
// Deliberately not annotated SxProps<Theme>: that is a union including arrays and
// callbacks, and spreading it into another sx object widens into nonsense. A plain
// inferred object literal spreads cleanly.
const highlightSx = {
    borderColor: 'warning.main',
    bgcolor: (theme: Theme) => alpha(theme.palette.warning.main, .14),
}

// The card paints its own header band, so padding lives on the inner sections
// rather than here, and overflow is clipped so the band follows the rounding.
const testLinkCardSx: SxProps<Theme> = {
    borderRadius: 2,
    overflow: 'hidden',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&:hover': {
        borderColor: 'primary.light',
        boxShadow: 1,
    },
}

interface TestLinkRowProps {
    test: DocumentTest
    functions: RiskAnalysisFunction[]
    linkEnabled: boolean
    highlightedFnId: number | null
}

const TestLinkRow = (props: TestLinkRowProps) => {
    const { test, functions, linkEnabled, highlightedFnId } = props
    const dialog = useContext(DialogContext)

    const linkedIds = linkedFunctionIds(test)
    // Driven by the function list rather than by the ids alone, so the rows come
    // out in document order and an id with no function behind it is dropped.
    const linked = functions.filter(fn => linkedIds.includes(fn.id))
    const stage = test.testSpecification?.testPhases?.[0]?.phaseType

    const highlighted = highlightedFnId !== null && linkedIds.includes(highlightedFnId)
    // Fading the rest is what turns a highlight into an answer: on a long list,
    // picking the marked cards out of a uniform column is still work.
    const dimmed = highlightedFnId !== null && !highlighted

    // Opened without a function when the test is linked to none — the dialog's
    // relatedObj is optional.
    const handleOpen = () => dialog({ tag: 'edit-test', test, fn: linked[0] })

    const body = <>
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            px: 1.5,
            py: 1.25,
            bgcolor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <FieldLabel>Nazwa testu</FieldLabel>
                <Box sx={{
                    fontSize: '.9rem',
                    fontWeight: 600,
                    lineHeight: 1.35,
                    overflowWrap: 'anywhere',
                }}>
                    {test.testSpecification?.name}
                </Box>
                {/* Inline: a stacked label above a single chip wasted a whole
                    line and made the header read as three competing blocks. */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mt: .75 }}>
                    <FieldLabel>Faza</FieldLabel>
                    {stage
                        ? <Chip size='small' label={stage} color='primary' variant='outlined' />
                        : <Dash />}
                </Box>
            </Box>
            <Tooltip title={<Localized id='open'>Otwórz</Localized>}>
                <IconButton size='small' onClick={handleOpen}>
                    <LaunchIcon fontSize='small' />
                </IconButton>
            </Tooltip>
        </Box>
        <Box sx={{ px: 1.5, py: 1.25 }}>
            {/* Nothing to fold when the list is empty, and that slot is the drop
                prompt — collapsing it away would hide the affordance. */}
            {linked.length === 0
                ? <>
                    <Box sx={{ mb: .75 }}><FieldLabel>Funkcje</FieldLabel></Box>
                    <EmptyFunctionsSlot linkEnabled={linkEnabled} />
                </>
                : <LinkedFunctionsSection
                    test={test}
                    linked={linked}
                    canUnlink={linkEnabled}
                    highlightedFnId={highlightedFnId} />}
        </Box>
    </>

    if (!linkEnabled) {
        return <Paper variant='outlined' sx={{
            ...testLinkCardSx,
            ...(highlighted ? highlightSx : {}),
            opacity: dimmed ? .45 : 1,
        }}>
            {body}
        </Paper>
    }

    return <ActiveTestLinkRow
        test={test}
        highlighted={highlighted}
        dimmed={dimmed}
    >
        {body}
    </ActiveTestLinkRow>
}

const LinkedFunctionsSection = (props: {
    test: DocumentTest
    linked: RiskAnalysisFunction[]
    canUnlink: boolean
    highlightedFnId: number | null
}) => {
    const { test, linked, canUnlink, highlightedFnId } = props
    // The fold is the user's alone: nothing — a drop, a highlight, a link made in
    // another view — opens a card they closed. The count badge in the header is
    // what reports a change made behind a fold.
    const [open, setOpen] = useState(true)
    const count = linked.length

    return <>
        <ButtonBase
            onClick={() => setOpen(o => !o)}
            aria-expanded={open}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: .75,
                width: '100%',
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderRadius: 1,
                px: .5,
                ml: -.5,
                py: .25,
                mb: .75,
            }}
        >
            <FieldLabel>Funkcje</FieldLabel>
            <CountBadge value={count} />
            <ExpandMoreIcon sx={{
                ml: 'auto',
                fontSize: '1.1rem',
                color: 'text.secondary',
                transform: open ? 'rotate(180deg)' : 'none',
                transition: 'transform .15s ease',
            }} />
        </ButtonBase>
        <Collapse in={open}>
            {/* One row per function rather than a row of chips: the names are rich
                text and routinely long enough that a chip would truncate them. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
                {linked.map(fn => <LinkedFunctionRow
                    key={fn.id}
                    test={test}
                    fn={fn}
                    canUnlink={canUnlink}
                    highlighted={fn.id === highlightedFnId} />)}
            </Box>
        </Collapse>
    </>
}

// Doubles as the drop affordance: a test with no functions is exactly where a
// user is most likely to be aiming, so it says so rather than sitting empty.
const EmptyFunctionsSlot = ({ linkEnabled }: { linkEnabled: boolean }) =>
    <Box sx={{
        fontSize: '.75rem',
        color: 'text.secondary',
        fontStyle: 'italic',
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        py: 1,
        textAlign: 'center',
    }}>
        {linkEnabled
            ? 'Przeciągnij tutaj funkcję, aby ją powiązać.'
            : 'Test nie jest powiązany z żadną funkcją.'}
    </Box>

const ActiveTestLinkRow = ({ test, highlighted, dimmed, children }: {
    test: DocumentTest
    highlighted: boolean
    dimmed: boolean
    children: ReactNode
}) => {
    const data: FnLinkDropData = { type: FN_LINK_DROP_TYPE, test }
    const { setNodeRef, isOver, active } = useDroppable({ id: fnLinkDropId(test.id), data })

    const dragged = active?.data.current as FnLinkDragData | undefined
    // Highlight only a drop that would actually do something, so hovering a test
    // that already covers the dragged function reads as "nothing to do here".
    const willLink = isOver
        && dragged?.type === FN_LINK_DRAG_TYPE
        && !linkedFunctionIds(test).includes(dragged.fn.id)

    return <Paper
        ref={setNodeRef}
        variant='outlined'
        sx={{
            ...testLinkCardSx,
            ...(highlighted ? highlightSx : {}),
            // A pending drop outranks a highlight: it is about to change something,
            // so it takes the border and the tint back off the amber.
            ...(willLink ? {
                // A primary tint rather than action.hover: the card's own header
                // band already uses that, and the two would cancel each other out.
                borderColor: 'primary.main',
                boxShadow: 3,
                bgcolor: theme => alpha(theme.palette.primary.main, .08),
            } : {}),
            // Never fade the card being dragged onto, whatever the filter says.
            opacity: dimmed && !willLink ? .45 : 1,
            transition: 'border-color .15s ease, box-shadow .15s ease, opacity .15s ease',
        }}
    >
        {children}
    </Paper>
}

// Unlinking down to zero is allowed here, unlike in TestSelected: there the last
// link vanishing would leave the test invisible from the risk analysis with no
// way back, whereas this view lists it either way and can re-link it.
const LinkedFunctionRow = (props: {
    test: DocumentTest
    fn: RiskAnalysisFunction
    canUnlink: boolean
    highlighted: boolean
}) => {
    const { test, fn, canUnlink, highlighted } = props
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const [unlinking, setUnlinking] = useState(false)

    const handleUnlink = async () => {
        try {
            setUnlinking(true)
            await dispatch(unlinkTestRelatedObject({
                documentId: Number(docId),
                testId: test.id,
                relatedObjectType: 'FUNCTION',
                relatedObjectId: fn.id,
            })).unwrap()
            dispatch(showSuccess('saved'))
        } catch {
            dispatch(showError('error'))
        } finally {
            setUnlinking(false)
        }
    }

    return <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '.75rem',
        // Outlined rather than filled: the card's drop highlight is a background
        // tint, which a filled child would disappear into.
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        pl: 1,
        pr: .5,
        py: .5,
        opacity: unlinking ? .5 : 1,
        transition: 'border-color .15s ease',
        ...(highlighted ? { ...highlightSx, fontWeight: 600 } : {}),
        '&:hover': { borderColor: 'primary.light' },
    }}>
        <Chip
            size='small'
            label={fn.numeration}
            sx={{ flexShrink: 0, fontWeight: 600 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}><FunctionName fn={fn} /></Box>
        {canUnlink && <Tooltip title={<Localized id='ra-test-unlink'>Odłącz od tej funkcji</Localized>}>
            <IconButton
                size='small'
                // Muted rather than hidden-until-hover: hover never fires on
                // touch, and an action nobody can see is an action nobody has.
                sx={{
                    flexShrink: 0,
                    color: 'text.disabled',
                    '&:hover': { color: 'error.main' },
                }}
                disabled={unlinking}
                onClick={handleUnlink}
            >
                <LinkOffIcon fontSize='small' />
            </IconButton>
        </Tooltip>}
    </Box>
}

const LinkableFunctionCard = (props: {
    fn: RiskAnalysisFunction
    linkEnabled: boolean
    highlighted: boolean
    testCount: number
    onToggleHighlight: (fnId: number) => void
}) => {
    const { fn, linkEnabled, highlighted, testCount, onToggleHighlight } = props
    // A function nobody intends to test is still listed, so the panel matches the
    // document, but it cannot be dragged onto a test.
    const draggable = linkEnabled && fn.isTestable

    // Stacked rather than one row: the names are rich text and often long enough
    // that a side-by-side number and name would leave the name a narrow column.
    const body = <Box sx={{ display: 'flex', flexDirection: 'column', gap: .5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
            {draggable && <DragIndicatorIcon fontSize='small' sx={{ color: 'text.disabled' }} />}
            <Chip size='small' label={fn.numeration} />
            {!fn.isTestable && <Chip size='small' label='Nietestowalna' />}
            {/* Grouped so one ml:auto right-aligns both, rather than each item
                competing for the free space. */}
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: .25 }}>
                <Tooltip title={`Powiązane testy: ${testCount}`}>
                    {/* Tooltip needs a ref-forwarding child, which CountBadge is not. */}
                    <Box sx={{ display: 'flex' }}>
                        <CountBadge value={testCount} />
                    </Box>
                </Tooltip>
                <Tooltip title={highlighted
                    ? 'Wyłącz podświetlenie'
                    : 'Podświetl testy powiązane z tą funkcją'}>
                    <IconButton
                        size='small'
                        aria-pressed={highlighted}
                        sx={{
                            color: highlighted ? 'warning.dark' : 'text.disabled',
                            '&:hover': { color: 'warning.dark' },
                        }}
                        // The card carries the drag listeners; without this the
                        // press that opens the highlight starts a drag instead.
                        onPointerDown={e => e.stopPropagation()}
                        onClick={e => { e.stopPropagation(); onToggleHighlight(fn.id) }}
                    >
                        <HighlightIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
        <Box sx={{ fontSize: '.8rem' }}><FunctionName fn={fn} /></Box>
    </Box>

    if (!draggable) {
        return <Paper variant='outlined' sx={{
            ...functionCardSx,
            ...(highlighted ? highlightSx : {}),
            opacity: fn.isTestable ? 1 : .55,
        }}>
            {body}
        </Paper>
    }

    return <DraggableFunctionCard fn={fn} highlighted={highlighted}>{body}</DraggableFunctionCard>
}

const functionCardSx: SxProps<Theme> = {
    p: 1,
    borderRadius: 2,
    transition: 'border-color .15s ease, box-shadow .15s ease',
}

const DraggableFunctionCard = ({ fn, highlighted, children }: {
    fn: RiskAnalysisFunction
    highlighted: boolean
    children: ReactNode
}) => {
    const data: FnLinkDragData = { type: FN_LINK_DRAG_TYPE, fn }
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `ra-fn-link-${fn.id}`,
        data,
    })

    return <Paper
        ref={setNodeRef}
        variant='outlined'
        sx={{
            ...functionCardSx,
            ...(highlighted ? highlightSx : {}),
            cursor: 'grab',
            touchAction: 'none',
            // Stays put rather than following the cursor: DragOverlay draws the
            // moving copy, and this is a link, so the function never leaves.
            opacity: isDragging ? .4 : 1,
            '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
            '&:active': { cursor: 'grabbing' },
        }}
        {...listeners}
    >
        {children}
    </Paper>
}

// Where a cell's context menu opens: at the event's coordinates. From the
// keyboard (Menu key, Shift+F10) those are wherever the browser decides —
// usually at the focused field, but possibly 0/0 — so when they fall outside
// the cell the menu opens at the cell's top-left corner instead.
//
// Whether it came from the keyboard only decides the highlight (see
// RaContextMenu). Browsers disagree on what a keyboard contextmenu event
// carries, so that is read off the last input: a right-click is always
// preceded by a pointerdown, the Menu key / Shift+F10 by a keydown.
let lastInputWasKeyboard = false
document.addEventListener('keydown', () => { lastInputWasKeyboard = true }, true)
document.addEventListener('pointerdown', () => { lastInputWasKeyboard = false }, true)

type ContextMenuPos = { mouseX: number, mouseY: number, keyboard?: boolean }

const contextMenuPosition = (event: React.MouseEvent): ContextMenuPos => {
    const rect = event.currentTarget.getBoundingClientRect()
    const { clientX: x, clientY: y } = event
    const inCell = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
    const keyboard = lastInputWasKeyboard
    return inCell
        ? { mouseX: x + 2, mouseY: y - 6, keyboard }
        : { mouseX: rect.left + 16, mouseY: rect.top + 16, keyboard }
}

// The context menu every RA box opens. MUI paints its keyboard highlight only
// when it sees the focus arrive by keyboard, which an autofocused first item
// does not — so opened from the keyboard, the focused item is highlighted
// here. Otherwise a one-item menu (nowhere for the arrows to move) never
// shows which item Enter would pick.
const RaContextMenu = ({ position, onClose, children }: {
    position: ContextMenuPos | null
    onClose: () => void
    children: ReactNode
}) =>
    <Menu
        open={position !== null}
        onClose={onClose}
        anchorReference="anchorPosition"
        anchorPosition={position !== null ? { top: position.mouseY, left: position.mouseX } : undefined}
        sx={position?.keyboard ? { '& .MuiMenuItem-root:focus': { bgcolor: 'action.focus' } } : undefined}
    >
        {children}
    </Menu>

// Static, editor-free rendering of a rich-text value. When empty it renders the
// same empty paragraph the editor produces (p.is-editor-empty + data-placeholder)
// so the existing placeholder CSS paints it identically — keeping the idle,
// loading and editing states pixel-consistent (no placeholder flicker on click).
const StaticTipTap = forwardRef<HTMLDivElement, {
    value: string
    placeholder?: string
    onClick?: () => void
    onFocus?: () => void
    onMouseDown?: (e: React.MouseEvent) => void
    tabIndex?: number
    style?: React.CSSProperties
}>((props, ref) => {
    const isEmpty = !props.value || props.value === '<p></p>'
    const common = {
        ref,
        className: 'tiptap ra-static-field',
        onClick: props.onClick,
        onFocus: props.onFocus,
        onMouseDown: props.onMouseDown,
        tabIndex: props.tabIndex,
        style: props.style,
    }
    if (isEmpty) {
        return <div {...common}>
            <p className="is-editor-empty" data-placeholder={props.placeholder ?? ''} />
        </div>
    }
    return <div {...common} dangerouslySetInnerHTML={{ __html: props.value }} />
})

type Point = { left: number, top: number }

// A rich-text field that only instantiates a Tiptap editor while it is needed.
// Idle fields render static HTML, so a document does not mount one ProseMirror
// editor per field up front (the dominant render cost).
//
// Modes:
// - idle: static HTML.
// - warm: the mouse is over the field, so a (not yet focused) editor is mounted
//   in its place. The mousedown then lands in a real editor and the browser
//   handles caret placement, drag-select and double/triple click natively.
// - editing: the editor has focus. Same instance as warm, so the selection the
//   browser just made survives the switch.
//
// Keyboard: an editable idle field is a tab stop and focusing it opens the
// editor, so Tab / Shift+Tab walk the fields in DOM order (the editor does not
// bind Tab). Escape closes the editor and leaves focus on the idle field.
const LazyText = (props: {
    value: string
    onChange: (html: string) => void
    editable: boolean
    placeholder?: string
}) => {
    const { value, onChange, editable, placeholder } = props
    const [mode, setMode] = useState<'idle' | 'warm' | 'editing'>('idle')
    const staticRef = useRef<HTMLDivElement>(null)
    const hovered = useRef(false)
    // Set by Escape: focus goes back to the idle field without reopening it.
    const returningFocus = useRef(false)
    // A mousedown on the idle field (no warm editor under the pointer: touch,
    // or a click right after Escape). The editor opens with the caret there.
    const clickPoint = useRef<Point | null>(null)
    const [openPoint, setOpenPoint] = useState<Point | null>(null)

    const current = editable ? mode : 'idle'

    useEffect(() => {
        if (current === 'idle' && returningFocus.current) staticRef.current?.focus()
    }, [current])

    const open = () => {
        if (returningFocus.current) {
            returningFocus.current = false
            return
        }
        setOpenPoint(clickPoint.current)
        clickPoint.current = null
        setMode('editing')
    }

    const handlePointerEnter = (e: React.PointerEvent) => {
        hovered.current = true
        // Mouse only: on touch, swapping the element between pointerenter and
        // the tap would break the tap.
        if (e.pointerType === 'mouse') setMode(m => m === 'idle' ? 'warm' : m)
    }
    const handlePointerLeave = () => {
        hovered.current = false
        setMode(m => m === 'warm' ? 'idle' : m)
    }

    const handleDone = () => {
        // Chromium fires focusout when Escape unmounts the focused editor;
        // that must not turn the field warm again.
        if (returningFocus.current) return
        setMode(m => m === 'editing' ? (hovered.current ? 'warm' : 'idle') : m)
    }

    // One stable element around both states, so swapping its content under
    // the pointer fires no pointerleave.
    return <div onPointerEnter={editable ? handlePointerEnter : undefined} onPointerLeave={editable ? handlePointerLeave : undefined}>
        {current === 'idle'
            ? <StaticTipTap
                ref={staticRef}
                value={value}
                placeholder={placeholder}
                tabIndex={editable ? 0 : undefined}
                onMouseDown={editable ? e => { clickPoint.current = { left: e.clientX, top: e.clientY } } : undefined}
                onFocus={editable ? open : undefined}
                // A click on a field that already has focus (after Escape) fires no
                // focus event, so the click opens it too.
                onClick={editable ? open : undefined}
                style={{ cursor: editable ? 'text' : 'default', minHeight: '1.2em' }}
            />
            : <LazyTextEditor
                value={value}
                placeholder={placeholder}
                onChange={onChange}
                active={current === 'editing'}
                initialPoint={openPoint}
                onActivate={() => setMode('editing')}
                onDone={handleDone}
                onEscape={() => {
                    returningFocus.current = true
                    setMode('idle')
                }}
            />}
    </div>
}

// Mounted only while a LazyText field is warm or being edited, so the editor
// hook (and the ProseMirror instance it builds) runs on demand rather than at
// list render.
const LazyTextEditor = (props: {
    value: string
    placeholder?: string
    onChange: (html: string) => void
    active: boolean
    // Where the caret goes when the editor opens already active (Tab, or a click
    // with no warm editor under it): the click point, else the end.
    initialPoint: Point | null
    onActivate: () => void
    onDone: () => void
    onEscape: () => void
}) => {
    const editorRef = useRef<Editor | null>(null)
    const editor = useJustText({
        defaultValue: props.value,
        onChange: () => props.onChange(editorRef.current?.getHTML() ?? ''),
        editable: true,
        placeholder: props.placeholder,
    })
    editorRef.current = editor
    // Opened warm, focus and selection are the browser's job.
    const openedActive = useRef(props.active)
    const mouseDown = useRef(false)

    useEffect(() => {
        if (!editor || !openedActive.current) return
        // The static rendering had the same layout, so the click point still
        // sits over the character that was clicked.
        const pos = props.initialPoint ? editor.view.posAtCoords(props.initialPoint)?.pos : undefined
        editor.commands.focus(pos ?? 'end')
    }, [editor])

    // useJustText only reads its value at mount: keep a warm editor in step
    // with saves landing while it is hovered.
    useEffect(() => {
        if (editor && !props.active && editor.getHTML() !== props.value) {
            editor.commands.setContent(props.value, false)
        }
    }, [props.value])

    const handleFocus = () => {
        const byMouse = mouseDown.current
        mouseDown.current = false
        if (props.active) return
        // Tab into a hovered field would leave the caret at the start; put it
        // at the end, as for an idle field.
        if (!byMouse) editor?.commands.focus('end')
        props.onActivate()
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            props.onEscape()
        }
    }

    // useEditor returns null until the editor exists. Show the same static
    // rendering until it is ready so neither the text nor the placeholder
    // blanks out during the swap.
    return <div
        className={props.active ? 'ra-editing-field' : undefined}
        onMouseDown={() => { mouseDown.current = true }}
        onFocus={handleFocus}
        onBlur={props.active ? props.onDone : undefined}
        onKeyDown={props.active ? handleKeyDown : undefined}>
        {editor
            ? <JustText editor={editor} />
            : <StaticTipTap value={props.value} placeholder={props.placeholder} />}
    </div>
}

interface FunctionProps {
    fn: RiskAnalysisFunction
    // The FMEA diagram view is not wrapped in a DndContext, so reordering is
    // off there. The hook still runs (hooks cannot be conditional) but is
    // disabled, and dnd-kit's contexts have defaults, so it is inert outside a
    // DndContext.
    sortable?: boolean
    // 'fmea' adds the test section beside the details grid. Recommended action
    // and the qualification flags are NOT function-level here: they live on the
    // reason (see the Reason component), matching the FMEA table, because that
    // is the field the mitigation schedule and the report generator read.
    variant?: 'simple' | 'fmea'
}
// Static given the layout variant — it carries no per-function data at all, so
// re-rendering it with the row shell is pure waste. Memoised for the same reason
// as FunctionBody below.
const FunctionHeaderRow = memo(({ variant, showTests }: { variant: LeafVariant, showTests: boolean }) => {
    const showComments = useShowRaComments()
    return <div className="flex flex-row">
        <div className="ra-basis-20 border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 py-1 text-xs font-bold flex justify-center items-center">Funkcja</div>
        <div className="ra-basis-65 ra-fn-details" style={{ gridTemplateColumns: diagramGridColTemplate(variant, showTests, showComments) }}>
            <Header variant={variant} />
            {showTests && <div className={headerCell}>Test</div>}
            {showComments && <div className={headerCell}><Localized id='ra-comments-column'>Komentarze</Localized></div>}
        </div>
    </div>
})

/**
 * The expensive half of a diagram row: the defect/consequence/reason tree and the
 * test column. Split out and memoised for the same reason as FunctionCellBody —
 * `useSortable` re-renders its caller on every DndContext render, which during a
 * drag is once per pointer move, and a memo on the row itself cannot stop that.
 * The shell absorbs those re-renders; this does not.
 */
const FunctionBody = memo((props: {
    fn: RiskAnalysisFunction
    variant: LeafVariant
    showTests: boolean
} & WithComments & Editable) => {
    const { fn, comments, editable, variant, showTests } = props
    const showComments = useShowRaComments()
    const defects = criticalDefects(fn)

    return <>
        <div className="ra-basis-65 ra-fn-details" style={{ gridTemplateColumns: diagramGridColTemplate(variant, showTests, showComments) }}>
            <FnContext.Provider value={fn.id}>
                {defects.length > 0 ? defects.map((item, idx) =>
                    <Defect key={item.id} editable={editable} fn={fn} defect={item} idx={idx} total={defects.length} comments={comments} variant={variant} />
                ) : <NoDefect editable={editable} fn={fn} variant={variant} />}
            </FnContext.Provider>
            {/* As in FunctionCellBody: inside the details grid, spanning every
                row of the function, so the comments column can follow it.
                TestSection — and the d&d drop zone in it — is the same
                component it was as a separate flex column. */}
            {showTests && <div className="flex-column" style={{
                gridColumn: diagramDataTracks[variant] + 1,
                gridRow: `1 / span ${fnRowSpan(defects)}`,
                minWidth: 0,
            }}>
                <TestSection fn={fn} editable={editable} />
            </div>}
        </div>
    </>
})

// What dnd-kit contributes to a row. Empty in the views that never reorder, so
// the row renders identically without anything having subscribed to a DndContext.
// Not its `attributes`: those make the element a tab stop (tabIndex 0, role
// button) for keyboard dragging, which no sensor here supports, so Tab would
// stop on every row for nothing. The same goes for the other draggables.
type FnDnd = Partial<Pick<ReturnType<typeof useSortable>,
    'setNodeRef' | 'listeners' | 'transform' | 'transition' | 'isDragging'>>
const NO_DND: FnDnd = {}

// The row shell: the column headings, the function name box (which carries the
// drag handle), and the context menu. Re-renders on every pointer move of a drag,
// so it deliberately holds nothing expensive.
const FunctionRow = (props: FunctionProps & WithComments & Editable & { dnd: FnDnd }) => {
    const { fn, comments, editable, sortable = true, variant = 'simple', dnd } = props
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const dialog = useContext(DialogContext)
    const testsEnabled = useContext(TestsEnabledContext)
    // The test column exists only in the FMEA layout, and only when the document
    // has tests turned on.
    const showTests = variant === 'fmea' && testsEnabled
    const isFmea = variant === 'fmea'

    // Patch-based so each field sends only what it changed; everything else is
    // carried through unchanged via `...data`.
    const save = useCallback(debounce((patch: Partial<RiskAnalysisFunction>, fn: RiskAnalysisFunction) => {
        const { defects, ...data } = fn
        dispatch(updateFunction({
            ...data,
            ...patch,
            docId,
        }))
            .then(unwrapResult)
            .then(() => {
                dispatch(showSuccess('saved'))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleNameChange = (html: string) => save({ name: html }, fn)

    const handleDeleteClick = () => {
        dialog({ tag: 'delete', item: { type: 'function', data: fn } })
        setContextMenu(null)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const draggable = editable && sortable

    return <Box
        ref={sortable ? dnd.setNodeRef : undefined}
        className={clsx(isFmea && 'ra-fmea')}
        sx={{
            transform: sortable ? CSS.Transform.toString(dnd.transform ?? null) : undefined,
            transition: sortable ? dnd.transition : undefined,
            opacity: dnd.isDragging ? 0.4 : 1,
            position: 'relative',
            zIndex: dnd.isDragging ? 1 : 'auto',
            bgcolor: 'background.paper',
        }}
    >
        <FunctionHeaderRow variant={variant} showTests={showTests} />
        <Spacer />
        <div className="flex flex-row items-start">
            {/* Lights up while a comment on the function is open, like the
                defect/consequence/reason boxes and the table's function cell. */}
            <RaAnchoredCell anchorKey={anchorKeyOf('function', fn.guid)} onContextMenu={handleContextMenu} className={clsx("ra-edit-box ra-basis-20 my-2 border-solid rounded-3xl border-2 border-gray-500 p-4 cursor-context-menu")}>
                <div className='flex space-x-2 items-center'>
                    {draggable && <Box
                        component='span'
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'grab',
                            touchAction: 'none',
                            color: 'action.disabled',
                        }}
                        {...dnd.listeners}
                    >
                        <DragIndicatorIcon fontSize='small' />
                    </Box>}
                    <div className='text-xs bg-gray-300 py-1 px-2 grow-0 rounded-lg text-sm'>
                        {fn.numeration}
                    </div>
                </div>
                <div className=" overflow-y-auto">
                    <LazyText value={fn.name} onChange={handleNameChange} editable={editable} placeholder='Opis funkcji...' />
                </div>
            </RaAnchoredCell>
            <FunctionBody
                fn={fn}
                comments={comments}
                editable={editable}
                variant={variant}
                showTests={showTests}
            />
            <RaContextMenu position={contextMenu} onClose={handleClose}>
                <MenuItem disabled={false} onClick={handleDeleteClick}><Localized id='delete'>Usuń</Localized></MenuItem>
            </RaContextMenu>
        </div>
        <HLine />
    </Box>
}

const SortableFunction = (props: FunctionProps & WithComments & Editable) => {
    const { fn, editable } = props
    const { setNodeRef, transform, transition, isDragging, listeners } = useSortable({
        id: fn.id,
        disabled: !editable,
    })
    return <FunctionRow
        {...props}
        dnd={{ setNodeRef, transform, transition, isDragging, listeners }}
    />
}

// Two components rather than one with a flag: the FMEA diagram never reorders, and
// `useSortable` subscribes its caller to the dnd-kit DndContext — which that view
// does mount, for test linking — so calling the hook there would re-render every
// row on every pointer move of a drag that has nothing to do with it.
const Function = (props: FunctionProps & WithComments & Editable) =>
    props.sortable === false
        ? <FunctionRow {...props} dnd={NO_DND} />
        : <SortableFunction {...props} />

function defsReasonsCount(def: RiskAnalysisDefect): number {
    let total = 0
    for (let i = 0; i < def.consequences.length; i++) {
        const cons = def.consequences[i];
        total += cons.reasons.length
    }
    return total
}

// Rows a function occupies in the table's inner grid, counted the way the rows
// are actually emitted: one per reason, one per reasonless consequence
// (NoReasonCell), one per consequenceless defect (NoConsequenceCell), and one
// for a function with no defects (NoDefectCell). Not defsReasonsCount — that
// counts a reasonless consequence as zero rows.
function fnRowSpan(defects: RiskAnalysisDefect[]): number {
    let total = 0
    for (const d of defects) {
        let rows = 0
        for (const c of d.consequences) rows += Math.max(c.reasons.length, 1)
        total += Math.max(rows, 1)
    }
    return Math.max(total, 1)
}

const fnCellStyles: SxProps<Theme> = {
    borderRight: table_border,
    borderBottom: table_border,
    p: .5,
    // See outer_grid_col_template: the tracks may now shrink below their
    // content, so the cells have to be free to shrink with them.
    minWidth: 0,
    overflowWrap: 'anywhere',
}

/**
 * Everything in a table row except the sortable shell.
 *
 * Split out and memoised because `useSortable` subscribes its caller to the
 * dnd-kit DndContext, and that context re-renders every consumer whenever the
 * provider re-renders — which during a drag is once per pointer move. A memo on
 * FunctionCell itself therefore does nothing (measured: the comparator runs and
 * returns true, and React re-renders anyway through the context). Keeping the
 * hook in the shell and the content down here lets the shell absorb those
 * re-renders while the body — the defect/consequence/reason grid and the test
 * column, i.e. all of the cost — is skipped.
 *
 * Neither memoising SortableContext's `items` nor stabilising the DndContext
 * handler props helps; both were measured and changed nothing.
 */
const FunctionCellBody = memo((props: { fn: RiskAnalysisFunction } & WithComments & Editable) => {
    const { fn, comments, editable } = props
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const testsEnabled = useContext(TestsEnabledContext)
    const showComments = useShowRaComments()

    // Only the name is editable in the table view; currentControlMethod is
    // carried through unchanged via `...data`.
    const save = useCallback(debounce((name: string, fn: RiskAnalysisFunction) => {
        const { defects, ...data } = fn
        dispatch(updateFunction({
            ...data,
            name,
            docId,
        }))
            .then(unwrapResult)
            .then(() => {
                dispatch(showSuccess('saved'))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleNameChange = (html: string) => save(html, fn)

    const defects = criticalDefects(fn)

    return <>
        <Box sx={fnCellStyles}>{fn.numeration}</Box>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('function', fn.guid)} sx={fnCellStyles}>
            <LazyText value={fn.name} onChange={handleNameChange} editable={editable} placeholder='Opis funkcji...' />
        </RaAnchoredCell>
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: innerGridColTemplate(testsEnabled, showComments),
            // Keeps the nested grid inside its outer track. Its columns are a
            // grid context of their own, matching the header's only because both
            // resolve the same content-independent template.
            minWidth: 0,
        }}>
            <FnContext.Provider value={fn.id}>
                {defects.length > 0 ? defects.map((item, idx) =>
                    <DefectCell key={item.id} editable={editable} fn={fn} defect={item} idx={idx} total={defects.length} comments={comments} />
                ) : <NoDefectCell fn={fn} editable={editable} />}
            </FnContext.Provider>
            {/* One cell down the whole function, as it was when it sat in the
                outer grid. Fixed on both axes, so the grid places it before the
                reason rows auto-flow around it however late it comes in the
                DOM — and last in the DOM puts it last in the Tab order, after
                the function's rows. TestSection — and with it the d&d drop
                zone — is unchanged. */}
            {testsEnabled && <Box sx={{
                ...fnCellStyles,
                gridColumn: TESTS_TRACK,
                gridRow: `1 / span ${fnRowSpan(defects)}`,
            }}>
                <TestSection fn={fn} editable={editable} />
            </Box>}
        </Box>
    </>
})

// The sortable shell: the drag handle plus the transform dnd-kit drives. Cheap
// enough to re-render on every pointer move; FunctionCellBody is not.
const FunctionCell = (props: FunctionProps & WithComments & Editable) => {
    const { fn, comments, editable } = props

    const { setNodeRef, transform, transition, isDragging, listeners } = useSortable({
        id: fn.id,
        disabled: !editable,
    })

    return <Box
        ref={setNodeRef}
        sx={{
            display: 'grid',
            gridTemplateColumns: 'subgrid',
            gridColumn: '1 / -1',
            transform: CSS.Transform.toString(transform),
            transition,
            opacity: isDragging ? 0.4 : 1,
            position: 'relative',
            zIndex: isDragging ? 1 : 'auto',
            bgcolor: 'background.paper',
        }}
    >
        <Box
            sx={{
                ...fnCellStyles,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                cursor: editable ? 'grab' : 'default',
                touchAction: 'none',
                color: 'action.disabled',
            }}
            {...(editable ? listeners : {})}
        >
            {editable && <DragIndicatorIcon fontSize='small' sx={{ display: 'block' }} />}
        </Box>
        <FunctionCellBody fn={fn} comments={comments} editable={editable} />
    </Box>
}

// The two actions offered where a function has no defects yet: build an empty
// defect chain, or ask the AI for a set. Its own component rather than markup
// repeated per placeholder, so the diagram and the table share one copy of the
// store-update rules below — both are easy to get subtly wrong, and have been.
const DefectActions = ({ fnId, editable }: { fnId: number } & Editable) => {
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)

    const [saving, setSaving] = useState(false)

    const create = async () => {
        setSaving(true)
        try {
            const newDefect = await createEmptyDefectChain(dispatch, docId, fnId)
            dispatch(pushDefect(newDefect))
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }
    const genAI = async () => {
        setSaving(true)
        try {
            const defects = await dispatch(genDefectAI({ docId, fnId })).unwrap()
            for (let i = 0; i < defects.length; i++) {
                const defect = defects[i]
                const criticalDefect = await dispatch(updateDefect({
                    docId,
                    id: defect.id,
                    functionId: fnId,
                    name: defect.name,
                    value: 1,
                })).unwrap()

                // RiskAnalysisDefectDto carries no FunctionId, so the response comes
                // back with it undefined however the type is declared. pushDefect
                // indexes the store by exactly that field, so pushing the response
                // as-is is a silent no-op — the defects reach the database and only
                // appear after a reload. Set it here the way createEmptyDefectChain
                // and the Ishikawa generator already do.
                dispatch(pushDefect({ ...criticalDefect, functionId: fnId }))
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    return <ButtonGroup>
        <LoadingButton disabled={!editable} loading={saving} variant='outlined' size='small' onClick={create}>+</LoadingButton>
        <LoadingButton disabled={!editable} loading={saving} variant='outlined' size='small' onClick={genAI}>AI</LoadingButton>
    </ButtonGroup>
}

// What a placeholder row can carry comments for: only the levels that exist
// above it, each on the first row it spans. Shared by the table's and the
// diagrams' placeholders.
const noDefectAnchors = (fn: RiskAnalysisFunction): RaAnchor[] =>
    [anchorOf('function', fn.guid, stripHtml(fn.name))]

const noConsequenceAnchors = (fn: RiskAnalysisFunction, defect: RiskAnalysisDefect, isFirstDefect: boolean): RaAnchor[] => {
    const defectAnchor = anchorOf('defect', defect.guid, stripHtml(defect.name))
    return isFirstDefect ? [...noDefectAnchors(fn), defectAnchor] : [defectAnchor]
}

const noReasonAnchors = (
    fn: RiskAnalysisFunction,
    defect: RiskAnalysisDefect,
    consequence: RiskAnalysisConsequence,
    isFirstDefect: boolean,
    isFirstConsequence: boolean,
): RaAnchor[] => {
    const out: RaAnchor[] = []
    if (isFirstDefect && isFirstConsequence) out.push(...noDefectAnchors(fn))
    if (isFirstConsequence) out.push(anchorOf('defect', defect.guid, stripHtml(defect.name)))
    out.push(anchorOf('consequence', consequence.guid, stripHtml(consequence.name)))
    return out
}

// The comments cell a diagram placeholder row ends with.
const DiagramPlaceholderComments = ({ anchors }: { anchors: RaAnchor[] }) => {
    const showComments = useShowRaComments()
    const perms = useRaCommentPerms()
    return showComments
        ? <RaCommentsCell anchors={anchors} canAdd={perms.canAdd} canResolve={perms.canResolve} sx={diagramCommentCellSx} />
        : null
}

// The trailing filler spans the rest of the data tracks rather than a fixed
// count: a fixed `col-span-4` was right for the plain diagram only, and left an
// FMEA-diagram row short, so the next row's cells flowed up into it.
const NoDefect = ({ fn, editable, variant = 'simple' }: { fn: RiskAnalysisFunction } & Editable & WithVariant) => {
    const anchors = useMemo(() => noDefectAnchors(fn), [fn])
    return <>
        <div></div>
        <div className="place-self-center">
            <DefectActions fnId={fn.id} editable={editable} />
        </div>
        <div style={{ gridColumn: `span ${diagramDataTracks[variant] - 2}` }}></div>
        <DiagramPlaceholderComments anchors={anchors} />
    </>
}

// The table's counterpart: one cell spanning the nine data columns, in place of
// the nine placeholders that used to read "empty". There is no defect yet, so
// none of the per-column values exist to fill them — and a single span cannot
// drift out of step with innerGridColTemplate the way hand-counted cells can.
//
// `span 9` rather than `1 / -1`: with the comments column on, `-1` would swallow
// the tenth track and the function's own comments would have nowhere to render.
const NoDefectCell = ({ fn, editable }: { fn: RiskAnalysisFunction } & Editable) => {
    const showComments = useShowRaComments()
    const perms = useRaCommentPerms()

    const anchors = useMemo(() => noDefectAnchors(fn), [fn])

    return <>
        <Box sx={{
            gridColumn: '1 / span 9',
            // Its own right and bottom, like every other table cell: the container
            // draws the left and top only.
            borderRight: table_border,
            borderBottom: table_border,
            p: .5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <DefectActions fnId={fn.id} editable={editable} />
        </Box>
        {showComments && <RaCommentsCell
            anchors={anchors}
            canAdd={perms.canAdd}
            canResolve={perms.canResolve}
            sx={emptyRowCommentCellStyles} />}
    </>
}

// The comments cell on a placeholder row: one row tall, same edges as its
// neighbours. The populated rows get theirs from ReasonCell, which has a gridRow
// span to match the entity above it.
const emptyRowCommentCellStyles: SxProps<Theme> = {
    borderRight: table_border,
    borderBottom: table_border,
    p: .5,
    minWidth: 0,
    overflowWrap: 'anywhere',
}

// As DefectActions, for a defect with no consequences yet. No AI button: asking
// for consequences hangs off the defect's own context menu in both views.
const ConsequenceActions = ({ defectId, fnId, editable }: { defectId: number, fnId: number } & Editable) => {
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)

    const [saving, setSaving] = useState(false)

    const create = async () => {
        setSaving(true)
        try {
            const newConsequence = await dispatch(createConsequence({
                docId,
                name: '',
                defectId,
                value: 0,
            })).unwrap()
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId: newConsequence.id,
                value: 0,
                currentControlMethod: '',
            })).unwrap()
            newConsequence.reasons = [newReason]
            // Rows are rendered from the store, so without this the consequence
            // reaches the database and nothing changes on screen until a reload —
            // assigning to the local object above leaves the store untouched.
            dispatch(pushConsequence({ fnId, defectId, consequence: newConsequence }))
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    return <LoadingButton disabled={!editable} loading={saving} variant='outlined' size='small' onClick={create}>+</LoadingButton>
}

const NoConsequence = (props: { fn: RiskAnalysisFunction, defect: RiskAnalysisDefect, isFirstDefect: boolean } & Editable & WithVariant) => {
    const { fn, defect, isFirstDefect, editable, variant = 'simple' } = props
    const anchors = useMemo(
        () => noConsequenceAnchors(fn, defect, isFirstDefect),
        [fn, defect, isFirstDefect])
    // The defect's line and box already sit in the first two tracks.
    return <>
        <div></div>
        <div className="place-self-center">
            <ConsequenceActions defectId={defect.id} fnId={fn.id} editable={editable} />
        </div>
        <div style={{ gridColumn: `span ${diagramDataTracks[variant] - 4}` }}></div>
        <DiagramPlaceholderComments anchors={anchors} />
    </>
}

// A consequence with no reasons yet. The diagrams had no placeholder for this at
// all — the row stopped after the consequence box, so the next reason row's cells
// flowed up into it — the same gap NoReasonCell closed in the table.
const NoReason = (props: {
    fn: RiskAnalysisFunction
    defect: RiskAnalysisDefect
    consequence: RiskAnalysisConsequence
    isFirstDefect: boolean
    isFirstConsequence: boolean
} & Editable & WithVariant) => {
    const { fn, defect, consequence, isFirstDefect, isFirstConsequence, editable, variant = 'simple' } = props
    const anchors = useMemo(
        () => noReasonAnchors(fn, defect, consequence, isFirstDefect, isFirstConsequence),
        [fn, defect, consequence, isFirstDefect, isFirstConsequence])
    // Defect and consequence, each with its line, already fill four tracks.
    return <>
        <div></div>
        <div className="place-self-center">
            <ReasonActions consequenceId={consequence.id} defectId={defect.id} fnId={fn.id} editable={editable} />
        </div>
        <div style={{ gridColumn: `span ${diagramDataTracks[variant] - 6}` }}></div>
        <DiagramPlaceholderComments anchors={anchors} />
    </>
}

// Spans the eight inner columns right of the defect, which already sits in the
// first one. The placeholders this replaces only covered seven, so such a row
// also stopped one column short of the table's right edge.
const NoConsequenceCell = (props: {
    fn: RiskAnalysisFunction
    defect: RiskAnalysisDefect
    isFirstDefect: boolean
} & Editable) => {
    const { fn, defect, isFirstDefect, editable } = props
    const showComments = useShowRaComments()
    const perms = useRaCommentPerms()

    // No consequence and no reason exist yet, so the row can only carry the two
    // levels above it — and the function only when this is its first defect.
    const anchors = useMemo(
        () => noConsequenceAnchors(fn, defect, isFirstDefect),
        [fn, defect, isFirstDefect])

    return <>
        <Box sx={{
            gridColumn: '2 / span 8',
            borderRight: table_border,
            borderBottom: table_border,
            p: .5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <ConsequenceActions defectId={defect.id} fnId={fn.id} editable={editable} />
        </Box>
        {showComments && <RaCommentsCell
            anchors={anchors}
            canAdd={perms.canAdd}
            canResolve={perms.canResolve}
            sx={emptyRowCommentCellStyles} />}
    </>
}

// As ConsequenceActions, for a consequence with no reasons yet.
const ReasonActions = ({ consequenceId, defectId, fnId, editable }: { consequenceId: number, defectId: number, fnId: number } & Editable) => {
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)

    const [saving, setSaving] = useState(false)

    const create = async () => {
        setSaving(true)
        try {
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId,
                value: 0,
                currentControlMethod: '',
            })).unwrap()
            // As in ConsequenceActions: rows render from the store, so without
            // this the reason reaches the database and the screen does not move
            // until a reload.
            dispatch(pushReason({ fnId, defectId, consequenceId, reason: newReason }))
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setSaving(false)
        }
    }

    return <LoadingButton disabled={!editable} loading={saving} variant='outlined' size='small' onClick={create}>+</LoadingButton>
}

// A consequence with no reasons yet. This had no table counterpart at all before
// — the row simply stopped after the consequence and severity cells, six tracks
// short — which the comments column would have made obvious.
const NoReasonCell = (props: {
    fn: RiskAnalysisFunction
    defect: RiskAnalysisDefect
    consequence: RiskAnalysisConsequence
    isFirstDefect: boolean
    isFirstConsequence: boolean
} & Editable) => {
    const { fn, defect, consequence, isFirstDefect, isFirstConsequence, editable } = props
    const showComments = useShowRaComments()
    const perms = useRaCommentPerms()

    const anchors = useMemo(
        () => noReasonAnchors(fn, defect, consequence, isFirstDefect, isFirstConsequence),
        [fn, defect, consequence, isFirstDefect, isFirstConsequence])

    return <>
        <Box sx={{
            gridColumn: '4 / span 6',
            borderRight: table_border,
            borderBottom: table_border,
            p: .5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            <ReasonActions consequenceId={consequence.id} defectId={defect.id} fnId={fn.id} editable={editable} />
        </Box>
        {showComments && <RaCommentsCell
            anchors={anchors}
            canAdd={perms.canAdd}
            canResolve={perms.canResolve}
            sx={emptyRowCommentCellStyles} />}
    </>
}

interface DefectProps {
    defect: RiskAnalysisDefect
    idx: number
    total: number
}
const Defect = (props: DefectProps & { fn: RiskAnalysisFunction } & WithComments & Editable & WithVariant) => {
    const { fn, defect, idx, total, comments, editable, variant = 'simple' } = props
    const dispatch = useAppDispatch()
    const functionId = useContext(FnContext)
    const docId = useContext(DocIdContext)

    const [generated, setGenerated] = useState(defect.generated)

    const [generating, setGenerating] = useState(false)

    const save = useCallback(debounce((name: string) => {
        dispatch(updateDefect({ docId, id: defect.id, functionId, name, value: defect.value }))
            .then(unwrapResult)
            .then((updated) => {
                dispatch(showSuccess('saved'))
                updated.functionId = functionId
                dispatch(setDefect(updated))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleDefectChange = (html: string) => {
        save(html)
        setGenerated(false)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextDefect = async () => {
        setContextMenu(null)
        try {
            const newDefect = await dispatch(createDefect({
                docId,
                name: '',
                functionId,
                // Critical, like every other defect these views create: a 0 here
                // would produce a defect this view then refuses to render.
                value: 1,
            })).unwrap()
            const newConsequence = await dispatch(createConsequence({
                docId,
                name: '',
                defectId: newDefect.id,
                value: 0,
            })).unwrap()
            newDefect.consequences.push(newConsequence)
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId: newConsequence.id,
                value: 0,
                currentControlMethod: '',

            })).unwrap()
            newConsequence.reasons = [newReason]
            newDefect.functionId = functionId
            dispatch(pushDefect(newDefect))
        } catch (error) {
            dispatch(showError('error'))
        } finally { }
    }

    const handleGenConsequenceAI = async () => {
        setContextMenu(null)
        setGenerating(true)
        try {
            const consequences = await dispatch(genConsequenceAI({
                docId,
                defectId: defect.id,
            })).unwrap()
            // An empty result means the server changed nothing.
            if (consequences.length > 0) {
                dispatch(replaceConsequences({ fnId: functionId, defectId: defect.id, consequences }))
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setGenerating(false)
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            const defId = await dispatch(deleteDefect({ docId, defectId: defect.id })).unwrap()
            dispatch(delDefect({
                fnId: functionId,
                defectId: defId,
            }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    // Comments on the defect render in the comments column, on its first row
    // (see Reason); the box lights up while one of them is open.
    const content = <RaAnchoredCell anchorKey={anchorKeyOf('defect', defect.guid)} onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative')}>
        <div className="">
            <LazyText value={defect.name} onChange={handleDefectChange} editable={editable} placeholder='Opis wady...' />
        </div>
        {generated && <span className="ra-ai-badge">AI</span>}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={false} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextDefect}><Localized id='risk-analysis-add-defect'>Dodaj kolejną wadę</Localized></MenuItem>
            <MenuItem onClick={handleGenConsequenceAI}><Localized id='risk-analysis-ai-add-defect'>Zapytaj o skutki AI</Localized></MenuItem>
        </RaContextMenu>
    </RaAnchoredCell>
    const consequences = defect.consequences.length > 0
        ? defect.consequences.map((item, i) =>
            <Consequence
                key={item.id}
                editable={editable}
                fn={fn}
                defect={defect}
                isFirstDefect={idx === 0}
                consequence={item}
                defectId={defect.id}
                idx={i}
                total={defect.consequences.length}
                isLastDefect={idx === total - 1}
                generatingConsequence={generating}
                comments={comments}
                variant={variant}
            />)
        : <NoConsequence fn={fn} defect={defect} isFirstDefect={idx === 0} editable={editable} variant={variant} />

    switch (idx) {
        case 0:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {total > 1 ? <TLine /> : <Line />}
                </div>
                {content}
                {consequences}
            </>
        case total - 1:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LLine />
                </div>
                {content}
                {consequences}
            </>
        default:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LeftTLine />
                </div>
                {content}
                {consequences}
            </>
    }
}
const DefectCell = (props: DefectProps & { fn: RiskAnalysisFunction } & WithComments & Editable) => {
    const { fn, defect, idx, total, comments, editable } = props
    const dispatch = useAppDispatch()
    const functionId = useContext(FnContext)
    const docId = useContext(DocIdContext)
    const commentsSetting = useContext(CommentsContext)
    const showComments = commentsSetting.tag === 'yes'

    const [generated, setGenerated] = useState(defect.generated)

    const [generating, setGenerating] = useState(false)

    const save = useCallback(debounce((name: string) => {
        dispatch(updateDefect({ docId, id: defect.id, functionId, name, value: defect.value }))
            .then(unwrapResult)
            .then((updated) => {
                updated.functionId = functionId
                dispatch(showSuccess('saved'))
                dispatch(setDefect(updated))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleDefectChange = (html: string) => {
        save(html)
        setGenerated(false)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextDefect = async () => {
        setContextMenu(null)
        try {
            const newDefect = await dispatch(createDefect({
                docId,
                name: '',
                functionId,
                // Critical, like every other defect these views create: a 0 here
                // would produce a defect this view then refuses to render.
                value: 1,
            })).unwrap()
            const newConsequence = await dispatch(createConsequence({
                docId,
                name: '',
                defectId: newDefect.id,
                value: 0,
            })).unwrap()
            newDefect.consequences.push(newConsequence)
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId: newConsequence.id,
                value: 0,
                currentControlMethod: '',

            })).unwrap()
            newConsequence.reasons = [newReason]
            newDefect.functionId = functionId
            dispatch(pushDefect(newDefect))
        } catch (error) {
            dispatch(showError('error'))
        } finally { }
    }

    const handleGenConsequenceAI = async () => {
        setContextMenu(null)
        setGenerating(true)
        try {
            const consequences = await dispatch(genConsequenceAI({
                docId,
                defectId: defect.id,
            })).unwrap()
            // An empty result means the server changed nothing.
            if (consequences.length > 0) {
                dispatch(replaceConsequences({ fnId: functionId, defectId: defect.id, consequences }))
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setGenerating(false)
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            await dispatch(deleteDefect({ docId, defectId: defect.id }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const content = <div onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative')}>
        <div className="">
            <LazyText value={defect.name} onChange={handleDefectChange} editable={editable} placeholder='Opis wady...' />
        </div>
        {generated && <span className="ra-ai-badge">AI</span>}
        {showComments && <CommentsSection comments={comments} documentId={docId} sectionGuid={defect.guid} />}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={false} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextDefect}><Localized id='risk-analysis-add-defect'>Dodaj kolejną wadę</Localized></MenuItem>
            <MenuItem onClick={handleGenConsequenceAI}><Localized id='risk-analysis-ai-add-defect'>Zapytaj o skutki AI</Localized></MenuItem>
        </RaContextMenu>
    </div>
    const consequences = defect.consequences.length > 0
        ? defect.consequences.map((item, i) =>
            <ConsequenceCell
                key={item.id}
                editable={editable}
                fn={fn}
                defect={defect}
                consequence={item}
                defectId={defect.id}
                idx={i}
                total={defect.consequences.length}
                isLastDefect={idx === total - 1}
                isFirstDefect={idx === 0}
                generatingConsequence={generating}
                comments={comments}
            />)
        : <NoConsequenceCell fn={fn} defect={defect} isFirstDefect={idx === 0} editable={editable} />

    // At least one: a defect with no consequences counts 0 reasons, and `span 0`
    // is not a valid track span — the placeholder row is exactly one row tall.
    const rsc = Math.max(defsReasonsCount(defect), 1)
    const gridRow = `span ${rsc} / span ${rsc}`
    const styles: SxProps<Theme> = {
        gridRow,
        borderRight: table_border,
        borderBottom: table_border,
        p: .5,
        // See innerGridColTemplate: the tracks may now shrink below their
        // content, so the cells have to be free to shrink with them.
        minWidth: 0,
        overflowWrap: 'anywhere',
    }

    return <>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('defect', defect.guid)} sx={styles}>
            <LazyText value={defect.name} onChange={handleDefectChange} editable={editable} placeholder='Opis wady...' />
            {/* <div>{`Reasons: ${rsc}`}</div> */}
        </RaAnchoredCell>
        {consequences}
    </>
}

interface ConsequenceProps {
    consequence: RiskAnalysisConsequence
    defectId: number
    idx: number
    total: number
    isLastDefect: boolean
    generatingConsequence: boolean
}
const Consequence = (props: ConsequenceProps & RaCellPath & WithComments & Editable & WithVariant) => {
    const { fn, defect, isFirstDefect, consequence, defectId, idx, total, isLastDefect, generatingConsequence, comments, editable, variant = 'simple' } = props
    const isFmea = variant === 'fmea'
    const dispatch = useAppDispatch()
    const fnId = useContext(FnContext)
    const docId = useContext(DocIdContext)

    const [generated, setGenerated] = useState(consequence.generated)

    const [generatingReason, setGeneratingReason] = useState(false)

    // Severity is only editable in the FMEA variant; the diagram carries it
    // through unchanged. The ref keeps the debounced save (deps: []) off a stale
    // closure, matching how name/ccm are handled in Reason.
    const [severity, setSeverity] = useState<number>(consequence.severity)
    const severityRef = useRef(consequence.severity)

    const save = useCallback(
        debounce((name: string) => {
            dispatch(updateConsequence({ docId, id: consequence.id, defectId, severity: severityRef.current, name }))
                .then(unwrapResult)
                .then((updated) => {
                    dispatch(showSuccess('saved'))
                    dispatch(setConsequence({
                        fnId,
                        defectId,
                        consequence: updated,
                    }))
                })
                .catch(() => dispatch(showError('error')))
        }, 400), [])

    const nameRef = useRef(consequence.name)

    const handleConsequenceChange = (html: string) => {
        nameRef.current = html
        save(html)
        setGenerated(false)
    }

    const handleSeverityChange = (value: number) => {
        severityRef.current = value
        setSeverity(value)
        save(nameRef.current)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextConsequence = async () => {
        setContextMenu(null)
        try {
            const newConsequence = await dispatch(createConsequence({
                docId,
                name: '',
                defectId,
                value: 0,
            })).unwrap()
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId: newConsequence.id,
                value: 0,
                currentControlMethod: '',
            })).unwrap()
            newConsequence.reasons = [newReason]
            dispatch(pushConsequence({
                fnId,
                defectId,
                consequence: newConsequence,
            }))
        } catch (error) {
            dispatch(showError('error'))
        } finally { }
    }

    const handleGenReasonsAI = async () => {
        setContextMenu(null)
        setGeneratingReason(true)
        try {
            const reasons = await dispatch(genReasonAI({
                docId,
                consequenceId: consequence.id,
            })).unwrap()
            // An empty result means the server changed nothing.
            if (reasons.length > 0) {
                dispatch(replaceReasons({ fnId, defectId, consequenceId: consequence.id, reasons }))
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setGeneratingReason(false)
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            const consId = await dispatch(deleteConsequence({ docId, id: consequence.id })).unwrap()
            dispatch(delConsequence({
                fnId,
                defectId,
                consequenceId: consId,
            }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const content = <RaAnchoredCell anchorKey={anchorKeyOf('consequence', consequence.guid)} onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative ')}>
        <div className="">
            <LazyText value={consequence.name} onChange={handleConsequenceChange} editable={editable} placeholder='Opis konsekwencji...' />
        </div>
        {isFmea && <Num
            editable={editable}
            value={severity}
            onChange={handleSeverityChange}
            css='absolute ra-severity w-6 cursor-pointer border border-solid rounded border-gray-200 shadow px-2'
        />}
        {generated && <span className="ra-ai-badge">AI</span>}
        {generatingConsequence && <CircularProgress sx={{
            position: 'absolute',
            top: 'calc(50% - 20px)',
            left: 'calc(50% - 20px)',
        }} />}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={idx === 0} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextConsequence}><Localized id=''>Dodaj kolejny skutek</Localized></MenuItem>
            <MenuItem onClick={handleGenReasonsAI}><Localized id='risk-analysis-ai-add-defect'>Zapytaj o przyczyny AI</Localized></MenuItem>
        </RaContextMenu>
    </RaAnchoredCell>
    const reasons = consequence.reasons.length > 0 ? consequence.reasons.map((item, i) =>
        <Reason
            key={item.id}
            editable={editable}
            fn={fn}
            defect={defect}
            consequence={consequence}
            isFirstDefect={isFirstDefect}
            isFirstConsequence={idx === 0}
            reason={item}
            defectId={defectId}
            consequenceId={consequence.id}
            idx={i}
            total={consequence.reasons.length}
            isLastDefect={isLastDefect}
            isLastReason={idx === total - 1}
            severity={severity}
            generating={generatingConsequence || generatingReason}
            comments={comments}
            variant={variant}
        />
    ) : <NoReason fn={fn} defect={defect} consequence={consequence} isFirstDefect={isFirstDefect} isFirstConsequence={idx === 0} editable={editable} variant={variant} />

    switch (idx) {
        case 0:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {total > 1 ? <TLine /> : <Line />}
                </div>
                {content}
                {reasons}
            </>
        case total - 1:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastDefect ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LLine />
                </div>
                {content}
                {reasons}
            </>
        default:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastDefect ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LeftTLine />
                </div>
                {content}
                {reasons}
            </>
    }
}
const ConsequenceCell = (props: ConsequenceProps & RaCellPath & WithComments & Editable) => {
    const { fn, defect, consequence, defectId, idx, total, isLastDefect, isFirstDefect, generatingConsequence, comments, editable } = props
    const dispatch = useAppDispatch()
    const fnId = useContext(FnContext)
    const docId = useContext(DocIdContext)
    const commentsSetting = useContext(CommentsContext)
    const showComments = commentsSetting.tag === 'yes'

    const [severity, setSeverity] = useState<number>(consequence.severity)
    const [generated, setGenerated] = useState(consequence.generated)

    const [generatingReason, setGeneratingReason] = useState(false)

    // name is edited via a lazily-mounted editor (tracked in a ref); severity is
    // a live Num control kept in state. Both are merged in a single save.
    const nameRef = useRef(consequence.name)

    const save = useCallback(
        debounce((name: string, s: number) => {
            dispatch(updateConsequence({ docId, id: consequence.id, defectId, severity: s, name }))
                .then(unwrapResult)
                .then((updated) => {
                    dispatch(showSuccess('saved'))
                    dispatch(setConsequence({
                        fnId,
                        defectId,
                        consequence: updated,
                    }))
                })
                .catch(() => dispatch(showError('error')))
        }, 400), [])

    const handleConsequenceChange = (html: string) => {
        nameRef.current = html
        setGenerated(false)
        save(html, severity)
    }
    const handleSeverityChange = (newVal: number) => {
        setSeverity(newVal)
        save(nameRef.current, newVal)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextConsequence = async () => {
        setContextMenu(null)
        try {
            const newConsequence = await dispatch(createConsequence({
                docId,
                name: '',
                defectId,
                value: 0,
            })).unwrap()
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId: newConsequence.id,
                value: 0,
                currentControlMethod: '',
            })).unwrap()
            newConsequence.reasons = [newReason]
            dispatch(pushConsequence({
                fnId,
                defectId,
                consequence: newConsequence,
            }))
        } catch (error) {
            dispatch(showError('error'))
        } finally { }
    }

    const handleGenReasonsAI = async () => {
        setContextMenu(null)
        setGeneratingReason(true)
        try {
            const reasons = await dispatch(genReasonAI({
                docId,
                consequenceId: consequence.id,
            })).unwrap()
            // An empty result means the server changed nothing.
            if (reasons.length > 0) {
                dispatch(replaceReasons({ fnId, defectId, consequenceId: consequence.id, reasons }))
            }
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setGeneratingReason(false)
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            await dispatch(deleteConsequence({ docId, id: consequence.id }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const Severity = <Num editable={editable} value={severity} onChange={handleSeverityChange} />

    const content = <div onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative ')}>
        <div className="">
            <LazyText value={consequence.name} onChange={handleConsequenceChange} editable={editable} placeholder='Opis konsekwencji...' />
        </div>
        {/* {Severity} */}
        {generated && <span className="ra-ai-badge">AI</span>}
        {showComments && <CommentsSection comments={comments} documentId={docId} sectionGuid={consequence.guid} />}
        {generatingConsequence && <CircularProgress sx={{
            position: 'absolute',
            top: 'calc(50% - 20px)',
            left: 'calc(50% - 20px)',
        }} />}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={idx === 0} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextConsequence}><Localized id=''>Dodaj kolejny skutek</Localized></MenuItem>
            <MenuItem onClick={handleGenReasonsAI}><Localized id='risk-analysis-ai-add-defect'>Zapytaj o przyczyny AI</Localized></MenuItem>
        </RaContextMenu>
    </div>
    const reasons = consequence.reasons.length > 0
        ? consequence.reasons.map((item, i) =>
            <ReasonCell
                key={item.id}
                editable={editable}
                fn={fn}
                defect={defect}
                consequence={consequence}
                reason={item}
                defectId={defectId}
                consequenceId={consequence.id}
                idx={i}
                total={consequence.reasons.length}
                isLastDefect={isLastDefect}
                isLastReason={idx === total - 1}
                isFirstDefect={isFirstDefect}
                isFirstConsequence={idx === 0}
                severity={severity}
                generating={generatingConsequence || generatingReason}
                comments={comments}
            />
        )
        : <NoReasonCell
            fn={fn}
            defect={defect}
            consequence={consequence}
            isFirstDefect={isFirstDefect}
            isFirstConsequence={idx === 0}
            editable={editable} />

    // As in DefectCell: a consequence with no reasons would ask for `span 0`.
    const rsc = Math.max(consequence.reasons.length, 1)
    const gridRow = `span ${rsc} / span ${rsc}`
    const styles: SxProps<Theme> = {
        gridRow,
        borderRight: table_border,
        borderBottom: table_border,
        p: .5,
        // See innerGridColTemplate: the tracks may now shrink below their
        // content, so the cells have to be free to shrink with them.
        minWidth: 0,
        overflowWrap: 'anywhere',
    }

    return <>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('consequence', consequence.guid)} sx={styles}>
            <LazyText value={consequence.name} onChange={handleConsequenceChange} editable={editable} placeholder='Opis konsekwencji...' />
        </RaAnchoredCell>
        <Box sx={{
            ...styles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {Severity}
        </Box>

        {reasons}
    </>
}

/**
 * The entities a row sits under, drilled down so the comments column can name
 * its anchor targets and decide which of them this row owns. The table cells and
 * the diagram components both carry it.
 */
interface RaCellPath {
    fn: RiskAnalysisFunction
    defect: RiskAnalysisDefect
    isFirstDefect: boolean
}

interface ReasonProps {
    reason: RiskAnalysisReason
    defectId: number
    consequenceId: number
    idx: number
    total: number
    isLastDefect: boolean
    isLastReason: boolean
    severity: number
    generating: boolean
}
type Qualification = 'DQ' | 'IQ' | 'OQ' | 'PQ'

// The reason-level DQ/IQ/OQ/PQ flags, shared by the diagram and the table so the
// two can't drift apart again — they already disagreed once about which entity
// owns this data. Read-only users get static chips: without onClick, MUI drops
// the hover and pointer affordance, so the row doesn't invite a save that the
// backend would reject anyway.
const QualificationChips = (props: {
    values: Record<Qualification, boolean>
    onToggle: (q: Qualification) => void
} & Editable) => {
    const { values, onToggle, editable } = props
    // Wrapping: the four chips would otherwise set this column's content floor
    // and spill over the cell border once the track shrinks.
    return <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {(['DQ', 'IQ', 'OQ', 'PQ'] as const).map(q =>
            <Chip
                key={q}
                label={q}
                size="small"
                variant={values[q] ? 'filled' : 'outlined'}
                color={values[q] ? 'success' : 'default'}
                onClick={editable ? () => onToggle(q) : undefined}
            />
        )}
    </Stack>
}

const Reason = (props: ReasonProps & RaCellPath & {
    consequence: RiskAnalysisConsequence
    isFirstConsequence: boolean
} & WithComments & Editable & WithVariant) => {
    const { fn, defect, consequence, isFirstDefect, isFirstConsequence, reason, defectId, consequenceId, idx, total, isLastDefect, isLastReason, severity, generating, editable, variant = 'simple' } = props
    const isFmea = variant === 'fmea'
    const dispatch = useAppDispatch()
    const testsEnabled = useContext(TestsEnabledContext)
    const fnId = useContext(FnContext)
    const docId = useContext(DocIdContext)
    const showCommentsColumn = useShowRaComments()
    const perms = useRaCommentPerms()

    // As in ReasonCell: each entity's comments render on the first row it spans.
    const { owned } = useMemo(() => rowAnchors(
        { fn, defect, consequence, reason },
        { isFirstDefect, isFirstConsequence, isFirstReason: idx === 0 },
        stripHtml,
    ), [fn, defect, consequence, reason, isFirstDefect, isFirstConsequence, idx])

    const [generated, setGenerated] = useState(reason.generated)

    // name, ccm and recommendedAction are all editable here but mounted lazily
    // one at a time, so their live values are tracked in refs and merged in a
    // single save. The remaining fields aren't edited in the diagram and pass
    // through from props.
    const nameRef = useRef(reason.name)
    const ccmRef = useRef(reason.currentControlMethod)
    const recRef = useRef(reason.recommendedAction)

    // Occurrence/detection/chips are only editable in the FMEA variant. Kept in
    // state for rendering and in refs so the debounced save (deps: []) reads live
    // values.
    const [occurence, setOccurence] = useState<number>(reason.occurrenceProbability)
    const [detection, setDetection] = useState<number>(reason.detectionProbability)
    const occurenceRef = useRef(reason.occurrenceProbability)
    const detectionRef = useRef(reason.detectionProbability)
    const [chipStates, setChipStates] = useState({
        DQ: reason.hasDQ,
        IQ: reason.hasIQ,
        OQ: reason.hasOQ,
        PQ: reason.hasPQ,
    })
    const chipsRef = useRef(chipStates)

    const save = useCallback(debounce((reason: RiskAnalysisReason) => {
        dispatch(updateReason({
            docId,
            id: reason.id,
            consequenceId,
            detectionProbability: detectionRef.current,
            occurrenceProbability: occurenceRef.current,
            name: nameRef.current,
            currentControlMethod: ccmRef.current,
            recommendedAction: recRef.current,
            hasDQ: chipsRef.current.DQ,
            hasIQ: chipsRef.current.IQ,
            hasOQ: chipsRef.current.OQ,
            hasPQ: chipsRef.current.PQ,
        }))
            .then(unwrapResult)
            .then((updated) => {
                dispatch(showSuccess('saved'))
                dispatch(setReason({
                    fnId,
                    defectId,
                    consequenceId,
                    reason: updated,
                }))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleNameChange = (html: string) => {
        nameRef.current = html
        setGenerated(false)
        save(reason)
    }
    const handleCcmChange = (html: string) => {
        ccmRef.current = html
        save(reason)
    }
    const handleRecommendedActionChange = (html: string) => {
        recRef.current = html
        save(reason)
    }
    const handleChipAction = (type: 'DQ' | 'IQ' | 'OQ' | 'PQ') => {
        if (!editable) return
        const updated = { ...chipsRef.current, [type]: !chipsRef.current[type] }
        chipsRef.current = updated
        setChipStates(updated)
        save(reason)
    }
    const handleOccurenceChange = (value: number) => {
        occurenceRef.current = value
        setOccurence(value)
        save(reason)
    }
    const handleDetectionChange = (value: number) => {
        detectionRef.current = value
        setDetection(value)
        save(reason)
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextReason = async () => {
        setContextMenu(null)
        try {
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId,
                value: 0,
                currentControlMethod: ''
            })).unwrap()
            dispatch(pushReason({
                fnId,
                defectId,
                consequenceId,
                reason: newReason,
            }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            const rId = await dispatch(deleteReason({ docId, id: reason.id })).unwrap()
            dispatch(delReason({
                fnId,
                defectId,
                consequenceId,
                reasonId: rId,
            }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const content = <RaAnchoredCell anchorKey={anchorKeyOf('reason', reason.guid)} onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative ')}>
        <div className="">
            <LazyText value={reason.name} onChange={handleNameChange} editable={editable} placeholder='Opis przyczyny...' />
        </div>
        {generated && <span className="ra-ai-badge">AI</span>}
        {generating && <CircularProgress sx={{
            position: 'absolute',
            top: 'calc(50% - 20px)',
            left: 'calc(50% - 20px)',
        }} />}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={idx === 0} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextReason}><Localized id=''>Dodaj kolejna przyczyne</Localized></MenuItem>
        </RaContextMenu>
    </RaAnchoredCell>

    const currentControlMethodContent = <RaAnchoredCell anchorKey={anchorKeyOf('control-method', reason.guid)} className={clsx(itemWrapperClass)}>
        <div>
            <LazyText value={reason.currentControlMethod} onChange={handleCcmChange} editable={editable} placeholder='Obecne środki kontroli...' />
        </div>
    </RaAnchoredCell>

    // Same cell the FMEA table builds in ReasonCell: the action text over the
    // qualification chips, both reason-level.
    const recommendedActionContent = <RaAnchoredCell anchorKey={anchorKeyOf('recommended-action', reason.guid)} className={clsx(itemWrapperClass, 'flex flex-col gap-1')}>
        <LazyText value={reason.recommendedAction} onChange={handleRecommendedActionChange} editable={editable} placeholder='Zalecane działania...' />
        {testsEnabled && <>
            <Divider sx={{ mb: .5 }} />
            <QualificationChips values={chipStates} onToggle={handleChipAction} editable={editable} />
        </>}
    </RaAnchoredCell>

    const numCss = ' w-7 cursor-pointer border border-solid rounded border-gray-200 shadow px-2'

    // Last on every row, after the data cells; FunctionBody's test cell sits
    // between the two on its own explicit track.
    const commentsCell = showCommentsColumn && <RaCommentsCell
        anchors={owned}
        canAdd={perms.canAdd}
        canResolve={perms.canResolve}
        sx={diagramCommentCellSx} />

    // The FMEA variant replaces the diagram's single spacer + control-method pair
    // with: occurrence, control method, detection, RPN, recommended action.
    const trailingCells = isFmea
        ? <>
            <div className="flex items-center justify-center">
                <Num editable={editable} value={occurence} onChange={handleOccurenceChange} css={numCss} />
            </div>
            {currentControlMethodContent}
            <div className="flex items-center justify-center">
                <Num editable={editable} value={detection} onChange={handleDetectionChange} css={numCss} />
            </div>
            <P f={occurence} d={detection} s={severity} />
            {recommendedActionContent}
            {commentsCell}
        </>
        : <>
            <div className=" grid grid-cols-2 gap-0 h-full">
                <Line />
            </div>
            {currentControlMethodContent}
            {commentsCell}
        </>

    switch (idx) {
        case 0:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {total > 1 ? <TLine /> : <Line />}
                </div>
                {content}
                {trailingCells}
            </>
        case total - 1:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastDefect ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastReason ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LLine />
                </div>
                {content}
                {trailingCells}
            </>
        default:
            return <>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastDefect ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    {isLastReason ? null : <VLine />}
                </div>
                <div className=""></div>
                <div className=" grid grid-cols-2 gap-0 h-full">
                    <LeftTLine />
                </div>
                {content}
                {trailingCells}
            </>
    }
}
const ReasonCell = (props: ReasonProps & RaCellPath & {
    consequence: RiskAnalysisConsequence
    isFirstConsequence: boolean
} & WithComments & Editable) => {
    const { fn, defect, consequence, reason, defectId, consequenceId, idx, total, isLastDefect, isLastReason, isFirstDefect, isFirstConsequence, severity, generating, comments, editable } = props
    const dispatch = useAppDispatch()
    const testsEnabled = useContext(TestsEnabledContext)
    const fnId = useContext(FnContext)
    const docId = useContext(DocIdContext)
    const commentsSetting = useContext(CommentsContext)
    const showComments = commentsSetting.tag === 'yes'
    const showCommentsColumn = useShowRaComments()
    const perms = useRaCommentPerms()

    // An entity spans several reason rows, so its comments render on the first of
    // them — and a comment started from its tile is composed there too.
    const { owned } = useMemo(() => rowAnchors(
        { fn, defect, consequence, reason },
        { isFirstDefect, isFirstConsequence, isFirstReason: idx === 0 },
        stripHtml,
    ), [fn, defect, consequence, reason, isFirstDefect, isFirstConsequence, idx])

    const [occurence, setOccurence] = useState<number>(reason.occurrenceProbability)
    const [detection, setDetection] = useState<number>(reason.detectionProbability)
    const [generated, setGenerated] = useState(reason.generated)
    const [chipStates, setChipStates] = useState({
        IQ: reason.hasIQ,
        OQ: reason.hasOQ,
        PQ: reason.hasPQ,
        DQ: reason.hasDQ,
    })

    // name, ccm and recommendedAction are edited via lazily-mounted editors
    // (mounted one at a time), so their live values are tracked in refs.
    // occurrence/detection/chips stay in state because they render live controls.
    // All are merged in a single save.
    const nameRef = useRef(reason.name)
    const ccmRef = useRef(reason.currentControlMethod)
    const recRef = useRef(reason.recommendedAction)

    const save = useCallback(debounce((occurence: number, detection: number, chips: { IQ: boolean; OQ: boolean; PQ: boolean; DQ: boolean }) => {
        dispatch(updateReason({
            docId,
            id: reason.id,
            consequenceId,
            detectionProbability: detection,
            occurrenceProbability: occurence,
            name: nameRef.current,
            currentControlMethod: ccmRef.current,
            recommendedAction: recRef.current,
            hasDQ: chips.DQ,
            hasIQ: chips.IQ,
            hasOQ: chips.OQ,
            hasPQ: chips.PQ,
        }))
            .then(unwrapResult)
            .then((updated) => {
                dispatch(showSuccess('saved'))
                dispatch(setReason({
                    fnId,
                    defectId,
                    consequenceId,
                    reason: updated,
                }))
            })
            .catch(() => dispatch(showError('error')))
    }, 400), [])

    const handleNameChange = (html: string) => {
        nameRef.current = html
        setGenerated(false)
        save(occurence, detection, chipStates)
    }
    const handleCcmChange = (html: string) => {
        ccmRef.current = html
        save(occurence, detection, chipStates)
    }
    const handleRecommendedActionChange = (html: string) => {
        recRef.current = html
        save(occurence, detection, chipStates)
    }
    const handleOccurenceChange = (newVal: number) => {
        setOccurence(newVal)
        save(newVal, detection, chipStates)
    }
    const handleDetectionChange = (newVal: number) => {
        setDetection(newVal)
        save(occurence, newVal, chipStates)
    }
    const handleChipAction = (type: Qualification) => {
        if (!editable) return
        setChipStates(prev => {
            const updated = { ...prev, [type]: !prev[type] }
            save(occurence, detection, updated)
            return updated
        })
    }

    // Context menu
    const [contextMenu, setContextMenu] = useState<ContextMenuPos | null>(null)

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault()
        if (editable) {
            setContextMenu(
                contextMenu === null
                    ? contextMenuPosition(event)
                    : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                    // Other native context menus might behave different.
                    // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                    null,
            )
        }
    }

    const handleClose = () => {
        setContextMenu(null)
    }
    // Context menu

    const handleAddNextReason = async () => {
        setContextMenu(null)
        try {
            const newReason = await dispatch(createReason({
                docId,
                name: '',
                consequenceId,
                value: 0,
                currentControlMethod: ''
            })).unwrap()
            dispatch(pushReason({
                fnId,
                defectId,
                consequenceId,
                reason: newReason,
            }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const handleDelete = async () => {
        setContextMenu(null)
        try {
            await dispatch(deleteReason({ docId, id: reason.id }))
        } catch (error) {
            dispatch(showError('error'))
        }
    }

    const content = <div onContextMenu={handleContextMenu} className={clsx(itemWrapperClass, 'cursor-context-menu relative ')}>
        <div className="">
            <LazyText value={reason.name} onChange={handleNameChange} editable={editable} placeholder='Opis przyczyny...' />
        </div>
        {generated && <span className="ra-ai-badge">AI</span>}
        {showComments && <CommentsSection comments={comments} documentId={docId} sectionGuid={reason.guid} />}
        {generating && <CircularProgress sx={{
            position: 'absolute',
            top: 'calc(50% - 20px)',
            left: 'calc(50% - 20px)',
        }} />}
        <RaContextMenu position={contextMenu} onClose={handleClose}>
            <MenuItem disabled={idx === 0} onClick={handleDelete}><Localized id='delete'>Usuń</Localized></MenuItem>
            <MenuItem onClick={handleAddNextReason}><Localized id=''>Dodaj kolejna przyczyne</Localized></MenuItem>
        </RaContextMenu>
    </div>

    const occurenceCss = ' w-7 cursor-pointer border border-solid rounded border-gray-200 shadow px-2'
    const Occurence = <Num editable={editable} value={occurence} onChange={handleOccurenceChange} css={occurenceCss} />

    const detectionCss = ' w-7 cursor-pointer border border-solid rounded border-gray-200 shadow px-2'
    const Detection = <Num editable={editable} value={detection} onChange={handleDetectionChange} css={detectionCss} />

    // const Severity = <Num editable={editable} value={detection} onChange={handleSeverityChange} />
    const p = <P f={occurence} d={detection} s={severity} />

    const styles: SxProps<Theme> = {
        borderRight: table_border,
        borderBottom: table_border,
        p: .5,
        // See innerGridColTemplate: the tracks may now shrink below their
        // content, so the cells have to be free to shrink with them.
        minWidth: 0,
        overflowWrap: 'anywhere',
    }

    return <>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('reason', reason.guid)} sx={styles}>
            <LazyText value={reason.name} onChange={handleNameChange} editable={editable} placeholder='Opis przyczyny...' />
        </RaAnchoredCell>
        <Box sx={{
            ...styles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {Occurence}
        </Box>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('control-method', reason.guid)} sx={styles}>
            <LazyText value={reason.currentControlMethod} onChange={handleCcmChange} editable={editable} placeholder='Środki kontroli...' />
        </RaAnchoredCell>
        <Box sx={{
            ...styles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {Detection}
        </Box>
        <Box sx={{
            ...styles,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }}>
            {p}
        </Box>
        <RaAnchoredCell className="ra-edit-box" anchorKey={anchorKeyOf('recommended-action', reason.guid)} sx={{ ...styles, display: 'flex', flexDirection: 'column', gap: .25 }}>
            <LazyText value={reason.recommendedAction} onChange={handleRecommendedActionChange} editable={editable} placeholder='Zalecane działania...' />
            {testsEnabled && <>
                <Divider sx={{ mb: .5 }} />
                <QualificationChips values={chipStates} onToggle={handleChipAction} editable={editable} />
            </>}
        </RaAnchoredCell>
        {showCommentsColumn && <RaCommentsCell
            anchors={owned}
            canAdd={perms.canAdd}
            canResolve={perms.canResolve}
            sx={styles} />}
    </>
}

const headerCell = " border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 text-xs font-bold flex justify-center items-center"

const Header = ({ variant = 'simple' }: WithVariant) => {
    if (variant === 'fmea') {
        // Eleven columns, matching the .ra-fmea grid template. Every reason row
        // emits exactly this many cells (Defect 2 + Consequence 2 + Reason 7),
        // so a column added here needs a matching cell in Reason.
        return <>
            <div className={headerCell}></div>
            <div className={headerCell}>Potencjalna wada</div>
            <div className={headerCell}></div>
            <div className={headerCell}>Skutek</div>
            <div className={headerCell}>Dotkliwość</div>
            <div className={headerCell}>Przyczyna</div>
            <div className={headerCell}>P wyst.</div>
            <div className={headerCell}>Ob. śr. kontroli</div>
            <div className={headerCell}>P wykr.</div>
            <div className={headerCell}>P</div>
            <div className={headerCell}>Zal. dział.</div>
        </>
    }
    return <>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 text-xs font-bold flex justify-end items-center"></div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 py-1 text-xs font-bold flex justify-center items-center">Potencjalna wada</div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 text-xs font-bold flex justify-end items-center"></div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 text-xs font-bold flex justify-center items-center">Skutek</div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 text-xs font-bold flex justify-start items-center"></div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 text-xs font-bold flex justify-center items-center">Przyczyna</div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 text-xs font-bold flex justify-start items-center"></div>
        <div className=" border-b border-gray-300 border-solid border-x-0 border-t-0 bg-gray-100 px-2 text-xs font-bold flex justify-center items-center">Obecne środki kontroli</div>
    </>
}

interface LineProps {
    label?: ReactNode
}

const Line = (props: LineProps) => {
    return <>
        <div className="border-gray-500 border-b-2 border-x-0 border-t-0 border-dashed"></div>
        <div className="border-gray-500 border-b-2 border-x-0 border-t-0 border-dashed relative">
            {props.label}
        </div>
        <div></div>
        <div></div>
    </>
}

const LLine = (props: LineProps) => {
    return <>
        <div className="border-gray-500 border-r-2 border-y-0 border-l-0 border-dashed"></div>
        <div className="relative">
            {props.label}
        </div>
        <div></div>
        <div className="border-gray-500 border-t-2 border-x-0 border-b-0 border-dashed"></div>
    </>
}

const TLine = (props: LineProps) => {
    return <>
        <div className="border-gray-500 border-b-2 border-x-0 border-t-0 border-dashed"></div>
        <div className="border-gray-500 border-b-2 border-x-0 border-t-0 border-dashed relative">
            {props.label}
        </div>
        <div className="border-gray-500 border-r-2 border-l-0 border-dashed border-y-0"></div>
        <div></div>
    </>
}

const LeftTLine = (props: LineProps) => {
    return <>
        <div className="border-gray-500 border-r-2 border-y-0 border-l-0 border-dashed"></div>
        <div className="relative">
            {props.label}
        </div>
        <div className="border-gray-500 border-dashed border-y-0 border-r-2 border-l-0"></div>
        <div className="border-gray-500 border-t-2 border-x-0 border-b-0 border-dashed"></div>
    </>
}

const VLine = () => {
    return <>
        <div className="border-gray-500 border-r-2 border-y-0 border-l-0 border-dashed"></div>
        <div className=""></div>
        <div className="border-gray-500 border-r-2 border-l-0 border-dashed border-y-0"></div>
        <div></div>
    </>
}

const HLine = () => {
    return <div className="col-span-2 h-4 border-gray-300 border-solid border-x-0 border-t-0 border-b"></div>
}

const Spacer = () => {
    return <div className="h-4"></div>
}

interface PProps {
    f: number
    d: number
    s: number
}
const P = (props: PProps) => {
    const { f, d, s } = props
    const { good, warning } = useContext(LimitContext)
    const value = f * d * s
    const cls = clsx(' place-self-center',
        value <= good && 'text-green-600 font-bold bg-green-100 py-1 px-2 rounded',
        value <= warning && value > good && 'text-orange-600 font-bold bg-orange-100 py-1 px-2 rounded',
        value > warning && 'text-red-600 font-bold bg-red-100 py-1 px-2 rounded',
    )
    return <div className={cls}>{value}</div>
}

interface NumProps {
    value: number
    onChange: (newVal: number) => void
    css?: string
}
const Num = (props: NumProps & Editable) => {
    const { editable } = props
    const { value, onChange } = props
    const [open, setOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const handleClick = (e) => {
        setAnchorEl(e.currentTarget)
        setOpen(st => !st)
    }
    // A tab stop: 1–5 set the score outright (the fast path through an FMEA),
    // the arrows step it, Enter/Space open the list and Escape closes it.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (/^[1-5]$/.test(e.key)) {
            onChange(Number(e.key))
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            const next = Math.min(5, Math.max(1, value + (e.key === 'ArrowUp' ? 1 : -1)))
            if (next !== value) onChange(next)
        } else if (e.key === 'Enter' || e.key === ' ') {
            setAnchorEl(e.currentTarget)
            setOpen(st => !st)
        } else if (e.key === 'Escape') {
            setOpen(false)
        } else {
            return
        }
        e.preventDefault()
    }
    const css = clsx('ra-num', props.css ?? 'ra-severity w-6 cursor-pointer border border-solid rounded border-gray-200 shadow px-2')

    if (editable) {
        return <ClickAwayListener onClickAway={() => { setOpen(false); }}>
            <div
                className={css}
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                // Tabbing away closes the list. A click on the list does not
                // count: its mousedown is kept from taking focus (below).
                onBlur={() => setOpen(false)}
            >
                <span>{props.value}</span>
                <Popper open={open} anchorEl={anchorEl}>
                    <Box onMouseDown={e => e.preventDefault()} sx={{
                        fontSize: '0.75rem',
                        p: 0,
                        bgcolor: 'var(--gray-50)',
                        border: '1px solid var(--gray-100)',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                        borderRadius: 0.5,
                        ['& ul']: {
                            m: 0,
                            p: 0,
                            listStyleType: 'none',
                            ['& li']: {
                                px: 3,
                                cursor: 'pointer',
                                position: 'relative',
                            },
                            ['& li:hover']: {
                                bgcolor: 'var(--gray-200)',
                            },
                            ['& li.current::before']: {
                                content: '"✓"',
                                position: 'absolute',
                                left: '5px',
                            },
                        },
                    }}>
                        <ul>
                            <li className={clsx(value === 1 && 'current')} onClick={() => onChange(1)}>1</li>
                            <li className={clsx(value === 2 && 'current')} onClick={() => onChange(2)}>2</li>
                            <li className={clsx(value === 3 && 'current')} onClick={() => onChange(3)}>3</li>
                            <li className={clsx(value === 4 && 'current')} onClick={() => onChange(4)}>4</li>
                            <li className={clsx(value === 5 && 'current')} onClick={() => onChange(5)}>5</li>
                        </ul>
                    </Box>
                </Popper>
            </div>
        </ClickAwayListener>
    } else {
        return <div className={css}>
            <span>{props.value}</span>
        </div>
    }

}

interface TestSectionProps {
    fn: RiskAnalysisFunction
}
const TestSection = (props: TestSectionProps & Editable) => {
    const { fn, editable } = props
    const dialog = useContext(DialogContext)

    // A test can be linked to several functions, so match on the link list. The
    // check is against undefined, not emptiness: an empty list means the test is
    // linked to nothing and must appear nowhere, whereas a missing list means the
    // backend predates the link table and the old scalar is all there is. Treating
    // the two alike would resurface a test under the function it was just
    // unlinked from, since the scalar still names it until the next reload.
    // Selected as the whole (memoised, reference-stable) list and filtered here
    // rather than inside the selector: an inline `.filter()` returns a fresh array
    // on every store action, so react-redux re-rendered every TestSection — and
    // with it every function row — on dispatches that touch nothing in this view.
    const allTests = useAppSelector(docTestsSelectors.selectAll)
    const tests = useMemo(() => allTests.filter(
        t => t.relatedObjectIds !== undefined
            ? t.relatedObjectIds.includes(fn.id)
            : t.relatedObjectId === fn.id), [allTests, fn.id])


    const handleCreateNewTestClick = () => {
        dialog({ tag: 'create-test', fn })
    }

    const empty = fn.isTestable && tests.length === 0
    let content = <></>
    if (empty) {
        content = <EmptyTest fn={fn} />
    } else if (!fn.isTestable) {
        content = <FnNotTestable />
    } else {
        content = <>{tests.map(t => <TestItem key={t.id} test={t} fn={fn} editable={editable} />)}
            <Button onClick={handleCreateNewTestClick} variant='outlined' size='small'><Localized id=''>Dodaj kolejny test</Localized></Button>
        </>
    }
    return <TestDropZone fn={fn}>
        {content}
    </TestDropZone>
}

// The whole test container is the drop target, matching where a test visually
// belongs. Renders a plain Box in views that do not mount the drag provider, so
// useDroppable is never called outside a DndContext.
const TestDropZone = ({ fn, children }: TestSectionProps & { children: ReactNode }) => {
    const { enabled } = useContext(TestDndContextValue)

    if (!enabled) {
        return <Box sx={{ display: 'flex', flexDirection: 'column' }}>{children}</Box>
    }

    return <ActiveTestDropZone fn={fn}>{children}</ActiveTestDropZone>
}

const ActiveTestDropZone = ({ fn, children }: TestSectionProps & { children: ReactNode }) => {
    const { setNodeRef, isOver, active } = useDroppable({
        id: testDropId(fn.id),
        data: { type: TEST_DROP_TYPE, fnId: fn.id },
    })

    const dragged = active?.data.current as TestDragData | undefined
    const alreadyHere = dragged?.type === TEST_DRAG_TYPE
        && linkedFunctionIds(dragged.test).includes(fn.id)
    // Highlight only a drop that would actually do something, so hovering the
    // source function reads as "nothing to do here" rather than as a valid target.
    const willLink = isOver && dragged?.type === TEST_DRAG_TYPE && !alreadyHere

    return <Box
        ref={setNodeRef}
        sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 1,
            outline: willLink ? '2px dashed' : '2px dashed transparent',
            outlineColor: willLink ? 'primary.main' : 'transparent',
            bgcolor: willLink ? 'action.hover' : 'transparent',
            transition: 'background-color .15s ease',
        }}
    >
        {children}
    </Box>
}

const TestItem = (props: TestSectionProps & Editable & { test: DocumentTest }) => {
    const { fn, test } = props
    const { enabled } = useContext(TestDndContextValue)

    const cls = "my-2 border-solid rounded-3xl border border-gray-500 p-4"
    const content = <div className={clsx(cls, "ml-1 text-xs flex justify-center")}>
        <TestSelected test={test} fn={fn} />
    </div>

    if (!enabled) {
        return content
    }

    return <DraggableTestItem test={test} fromFnId={fn.id}>{content}</DraggableTestItem>
}

const DraggableTestItem = (props: { test: DocumentTest, fromFnId: number, children: ReactNode }) => {
    const { test, fromFnId, children } = props
    const data: TestDragData = { type: TEST_DRAG_TYPE, test, fromFnId }
    const { listeners, setNodeRef, isDragging } = useDraggable({
        id: `ra-test-${test.id}-${fromFnId}`,
        data,
    })

    return <Box
        ref={setNodeRef}
        sx={{
            cursor: 'grab',
            touchAction: 'none',
            // Kept in place rather than following the cursor — DragOverlay draws the
            // moving copy, and this is a link, so the original never leaves.
            opacity: isDragging ? 0.4 : 1,
        }}
        {...listeners}
    >
        {children}
    </Box>
}

const EmptyTest = (props: TestSectionProps) => {
    const { fn } = props
    const docId = useContext(DocIdContext)
    const dispatch = useAppDispatch()
    const dialog = useContext(DialogContext)

    const [loading, setLoading] = useState(false)

    const handleUntestableClick = async () => {
        const { defects, ...data } = fn
        try {
            setLoading(true)
            await dispatch(updateFunction({
                ...data,
                isTestable: false,
                docId,
            })).unwrap()
            setLoading(false)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
        }
    }

    return <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        ml: 1,
    }}>
        <LoadingButton loading={loading} variant='outlined' size='small' onClick={() => dialog({ tag: 'create-test', fn })}>Dodaj test</LoadingButton>
        <LoadingButton loading={loading} variant='outlined' size='small' onClick={handleUntestableClick}>Funkcja <br /> nietestowalna</LoadingButton>
    </Box>
}
const FnNotTestable = () => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }
    return <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        p: 2,
    }}>
        <Chip label='Nietestowalna' />
        <IconButton onClick={e => { e.stopPropagation(); handleClick(e); }} sx={{ p: 0, mx: 1 }}><MoreVertIcon fontSize='small' /></IconButton>
        <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&::before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                    },
                },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
            <MenuItem onClick={e => {
                e.stopPropagation();
                setAnchorEl(null);
            }}>Funkcja testowalna</MenuItem>
        </Menu>
    </Box>
}
const TestSelected = ({ test, fn, }: { test: DocumentTest, fn: RiskAnalysisFunction }) => {
    const dialog = useContext(DialogContext)
    const dispatch = useAppDispatch()
    const docId = useContext(DocIdContext)
    const { enabled } = useContext(TestDndContextValue)
    const [unlinking, setUnlinking] = useState(false)
    const stage = test.testSpecification.testPhases[0].phaseType

    const handleTestDbClick = useCallback(() => {
        dialog({ tag: 'edit-test', test, fn })
    }, [])

    // Only offered while the test still covers another function. Removing the last
    // link would leave the test attached to nothing and invisible from the risk
    // analysis, with no way to get it back — deleting a test outright stays a
    // separate action on the Tests tab.
    const canUnlink = enabled && linkedFunctionIds(test).length > 1

    const handleUnlink = async () => {
        try {
            setUnlinking(true)
            await dispatch(unlinkTestRelatedObject({
                documentId: Number(docId),
                testId: test.id,
                relatedObjectType: 'FUNCTION',
                relatedObjectId: fn.id,
            })).unwrap()
            dispatch(showSuccess('saved'))
        } catch {
            dispatch(showError('error'))
        } finally {
            setUnlinking(false)
        }
    }

    return <div className='flex flex-col gap-2 items-start w-full'>
        <div onDoubleClick={handleTestDbClick}>{test.testSpecification.name}</div>
        <Box sx={{
            display: 'flex',
            width: '100%',
        }}>
            <div className='bg-gray-300 px-1 text-xs grow-0 rounded-lg flex items-center justify-center'>{stage}</div>
            {canUnlink && <IconButton
                size='small'
                sx={{ ml: 'auto' }}
                disabled={unlinking}
                // The click must not reach the drag listeners on the wrapper.
                onPointerDown={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); handleUnlink() }}
            >
                <Tooltip title={<Localized id='ra-test-unlink'>Odłącz od tej funkcji</Localized>}>
                    <LinkOffIcon fontSize="small" />
                </Tooltip>
            </IconButton>}
            <IconButton
                size='small'
                sx={{ ml: canUnlink ? 0 : 'auto' }}
                onPointerDown={e => e.stopPropagation()}
                onClick={handleTestDbClick}
            >
                <Tooltip title={<Localized id='open'>Otwórz</Localized>}>
                    <LaunchIcon fontSize="small" />
                </Tooltip>
            </IconButton>
        </Box>
    </div>
}

const CreateTestDialog = (props: { fn: RiskAnalysisFunction }) => {
    const { fn } = props
    const docId = useContext(DocIdContext)
    const dispatch = useAppDispatch()
    const dialog = useContext(DialogContext)
    const { data: structure } = useAppSelector(selectContextStructure)

    const [newTestId, setNewTestId] = useState<AppId | undefined>(undefined)

    const close = useCallback(() => dialog({ tag: 'none' }), [])
    const handleTestCreated = useCallback((testId: AppId) => {
        dispatch(updateFunction({
            ...fn,
            docId,
            testDefinitionId: testId,
            isTestable: true,
        })).then(() => {
            setNewTestId(testId)
        })
    }, [])

    return <Dialog fullScreen open={true}>
        <DialogContent>
            {newTestId ?
                <EditValidationForm id={newTestId} onClose={close} onDeleted={() => { }} /> :
                <CreateValidationForm onSuccess={handleTestCreated} onCancel={close} defName={fn.name} defStructureId={structure?.structId} />}
        </DialogContent>
    </Dialog>
}

function DeleteItem(props: { item: Entity, parentGroupId?: AppId }) {
    const { item, parentGroupId } = props
    const dispatch = useAppDispatch()
    const documentId = useContext(DocIdContext)
    const dialog = useContext(DialogContext)
    const [deleting, setDeleting] = useState(false)

    const handleCancelDelClick = () => {
        dialog({ tag: 'none' })
    }

    const deleteFun = async (fn: RiskAnalysisFunction) => {
        setDeleting(true)
        try {
            await dispatch(deleteFunction({
                docId: documentId,
                functionId: fn.id,
            })).unwrap()
            handleCancelDelClick()
        } catch (error) {
            console.log(error)
        } finally {
            setDeleting(false)
        }

    }

    const handleConfirmDeleteClick = () => {
        switch (props.item.type) {
            case 'function':
                deleteFun(props.item.data)
                break
        }
    }

    return <Dialog open={true}>
        <DialogTitle>Usuwanie</DialogTitle>
        <DialogContent>
            <DialogContentText>
                <Localized id="confirm-delete">
                    <span>Czy napewno chcesz usunąć?</span>
                </Localized>
            </DialogContentText>
        </DialogContent>
        <DialogActions>
            <LoadingButton loading={deleting} onClick={handleConfirmDeleteClick}>
                <Localized id="yes"><span>Tak</span></Localized>
            </LoadingButton>
            <LoadingButton loading={deleting} onClick={handleCancelDelClick}>
                <Localized id="no"><span>Nie</span></Localized>
            </LoadingButton>
        </DialogActions>
    </Dialog>
}
